import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BarChart3, TrendingUp, Package, AlertTriangle, Users, Upload, Receipt } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { downloadStyledReport } from '../utils/reportExcel';
import { formatDateTime, formatDateOnly } from '../utils/dateTime';
import { Button, Badge, SearchBar } from '../components/common';
import ResponsiveTable from '../components/common/ResponsiveTable';
import ResponsiveSelect from '../components/common/ResponsiveSelect';
import { api } from '../services/api';
import ReportFiltersComponent from '../components/reports/ReportFilters';
import ReportKPIs from '../components/reports/ReportKPIs';
import ReportCharts from '../components/reports/ReportCharts';
import ReportDetailDrawer from '../components/reports/ReportDetailDrawer';
import ReportSkeleton from '../components/reports/ReportSkeleton';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../contexts/ToastContext';
import { inwardImportService } from '../services/inwardImportService';
import { outwardImportService } from '../services/outwardImportService';

type ReportType = 'inventory' | 'inward' | 'outward' | 'damage' | 'billing' | 'client-utilization' | 'additional-space';

interface ReportFilters {
  clientId?: number;
  dateFrom?: string;
  dateTo?: string;
  productDescription?: string;
  batchNo?: string;
  warehouseId?: number;
  search?: string;
  status?: string;
}

export default function ReportsPage() {
  const { hasPermission, user } = useAuth();
  const isAdmin = ['ADMIN', 'SUPER_ADMIN'].includes((user?.role?.name || '').toUpperCase());
  const [reportType, setReportType] = useState<ReportType>('inventory');
  const [filters, setFilters] = useState<ReportFilters>({});
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);
  const limit = 25;

  // Available export fields for each report type (only actual table columns)
  const getAvailableFields = (type: string) => {
    // strip _grid suffix so inward_grid reuses inward's field list
    const baseType = type.endsWith('_grid') ? type.slice(0, -5) : type;
    const fieldsByType: Record<string, { key: string; label: string; category: string }[]> = {
      inventory: [
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'productCategoryName', label: 'Category', category: 'Product' },
        { key: 'itemCode', label: 'Item Code', category: 'Product' },
        { key: 'skuCode', label: 'SKU Code', category: 'Product' },
        { key: 'customerSapCode', label: 'SAP Code', category: 'Product' },
        { key: 'productDescription', label: 'Product Description', category: 'Product' },
        { key: 'batchNo', label: 'Batch Number', category: 'Product' },
        { key: 'unitType', label: 'Unit Type', category: 'Product' },
        { key: 'packSize', label: 'Pack Size', category: 'Product' },
        { key: 'masterPacking', label: 'Master Packing', category: 'Product' },
        { key: 'mfgDate', label: 'MFG Date', category: 'Product' },
        { key: 'expiryDate', label: 'Exp Date', category: 'Product' },
        { key: 'weightPerItem', label: 'Weight/Item (kg)', category: 'Product' },
        { key: 'totalWeight', label: 'Total Weight (kg)', category: 'Stock' },
        { key: 'goodQty', label: 'Good Quantity', category: 'Stock' },
        { key: 'cdamQty', label: 'CDAM Quantity', category: 'Stock' },
        { key: 'damQty', label: 'DAM Quantity', category: 'Stock' },
        { key: 'totalQty', label: 'Total Quantity', category: 'Stock' },
        { key: 'grandTotal', label: 'Grand Total', category: 'Stock' },
        { key: 'palletCount', label: 'Pallet Count', category: 'Stock' },
        { key: 'storageLocations', label: 'Storage Locations', category: 'Location' },
        { key: 'updatedAt', label: 'Last Updated', category: 'Basic' }
      ],
      inventory_grid: [
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'productCategoryName', label: 'Category', category: 'Product' },
        { key: 'itemCode', label: 'Item Code', category: 'Product' },
        { key: 'skuCode', label: 'SKU Code', category: 'Product' },
        { key: 'customerSapCode', label: 'SAP Code', category: 'Product' },
        { key: 'productDescription', label: 'Product Description', category: 'Product' },
        { key: 'batchNo', label: 'Batch Number', category: 'Product' },
        { key: 'unitType', label: 'Unit Type', category: 'Product' },
        { key: 'mfgDate', label: 'MFG Date', category: 'Product' },
        { key: 'expiryDate', label: 'Exp Date', category: 'Product' },
        { key: 'weightPerItem', label: 'Weight/Item (kg)', category: 'Product' },
        { key: 'goodQty', label: 'Good Quantity', category: 'Stock' },
        { key: 'cdamQty', label: 'CDAM Quantity', category: 'Stock' },
        { key: 'damQty', label: 'DAM Quantity', category: 'Stock' },
        { key: 'totalQty', label: 'Total Quantity', category: 'Stock' },
        { key: 'grandTotal', label: 'Grand Total', category: 'Stock' },
        { key: 'palletCount', label: 'Pallet Count', category: 'Stock' },
        { key: 'storageLocations', label: 'Storage Locations', category: 'Location' }
      ],
      inward: [
        { key: 'grnNumber', label: 'GRN Number', category: 'Basic' },
        { key: 'gateEntryNumber', label: 'Gate Entry Number', category: 'Basic' },
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'itemCode', label: 'Item Code', category: 'Product' },
        { key: 'productCategoryName', label: 'Category', category: 'Product' },
        { key: 'productDescription', label: 'Product Description', category: 'Product' },
        { key: 'batchNo', label: 'Batch Number', category: 'Product' },
        { key: 'unitType', label: 'Unit Type', category: 'Product' },
        { key: 'weightPerItem', label: 'Weight/Item (kg)', category: 'Product' },
        { key: 'lotNo', label: 'Lot No', category: 'Product' },
        { key: 'handlingType', label: 'Handling Type', category: 'Product' },
        { key: 'packSize', label: 'Pack Size', category: 'Product' },
        { key: 'masterPacking', label: 'Master Packing', category: 'Product' },
        { key: 'mfgDate', label: 'MFG Date', category: 'Product' },
        { key: 'expiryDate', label: 'Expiry Date', category: 'Product' },
        { key: 'goodQty', label: 'Good Quantity', category: 'Stock' },
        { key: 'damQty', label: 'DAM Quantity', category: 'Stock' },
        { key: 'cdamQty', label: 'CDAM Quantity', category: 'Stock' },
        { key: 'shortExcess', label: 'Short/Excess', category: 'Stock' },
        { key: 'totalWeight', label: 'Total Weight (kg)', category: 'Stock' },
        { key: 'grandTotal', label: 'Grand Total', category: 'Stock' },
        { key: 'palletCount', label: 'Pallet Count', category: 'Stock' },
        { key: 'storageLocations', label: 'Storage Locations', category: 'Location' },
        { key: 'clientLocationName', label: 'Client Location', category: 'Location' },
        { key: 'grnStatus', label: 'GRN Status', category: 'Basic' },
        { key: 'createdAt', label: 'Date Created', category: 'Basic' },
        { key: 'createdBy', label: 'Created By', category: 'Basic' },
        { key: 'approvedBy', label: 'Approved By', category: 'Basic' },
        { key: 'approvedAt', label: 'Approved At', category: 'Basic' },
        { key: 'itemRemarks', label: 'Item Remarks', category: 'Basic' },
        { key: 'remarks', label: 'Gate Entry Remarks', category: 'Basic' },
        { key: 'vehicleNumber', label: 'Vehicle Number', category: 'Transport' },
        { key: 'driverName', label: 'Driver Name', category: 'Transport' },
        { key: 'driverPhone', label: 'Driver Phone', category: 'Transport' },
        { key: 'grnVehicleNo', label: 'GRN Vehicle No', category: 'Transport' },
        { key: 'grnTransporterName', label: 'GRN Transporter Name', category: 'Transport' },
        { key: 'grnLrNumber', label: 'GRN LR Number', category: 'Transport' },
        { key: 'grnLrDate', label: 'GRN LR Date', category: 'Transport' },
        { key: 'invoiceNumber', label: 'Invoice Number', category: 'Documents' },
        { key: 'invDate', label: 'Invoice Date', category: 'Documents' },
        { key: 'ewayBillNumber', label: 'E-Way Bill Number', category: 'Documents' },
        { key: 'packingSlipNumber', label: 'Packing Slip Number', category: 'Documents' },
        { key: 'expectedQuantity', label: 'Expected Quantity', category: 'Documents' },
        { key: 'dockNumber', label: 'Dock Number', category: 'Basic' }
      ],
      outward: [
        { key: 'dispatchNumber', label: 'Dispatch Number', category: 'Basic' },
        { key: 'gateEntryNumber', label: 'Gate Entry Number', category: 'Basic' },
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'itemCode', label: 'Item Code', category: 'Product' },
        { key: 'productCategoryName', label: 'Category', category: 'Product' },
        { key: 'productDescription', label: 'Product Description', category: 'Product' },
        { key: 'batchNo', label: 'Batch Number', category: 'Product' },
        { key: 'unitType', label: 'Unit Type', category: 'Product' },
        { key: 'weightPerItem', label: 'Weight/Item (kg)', category: 'Product' },
        { key: 'lotNo', label: 'Lot No', category: 'Product' },
        { key: 'handlingType', label: 'Handling Type', category: 'Product' },
        { key: 'packSize', label: 'Pack Size', category: 'Product' },
        { key: 'masterPacking', label: 'Master Packing', category: 'Product' },
        { key: 'mfgDate', label: 'MFG Date', category: 'Product' },
        { key: 'expiryDate', label: 'Expiry Date', category: 'Product' },
        { key: 'goodQty', label: 'Good Quantity', category: 'Stock' },
        { key: 'cdamQty', label: 'CDAM Quantity', category: 'Stock' },
        { key: 'damQty', label: 'DAM Quantity', category: 'Stock' },
        { key: 'grandTotal', label: 'Grand Total', category: 'Stock' },
        { key: 'palletCount', label: 'Pallet Count', category: 'Stock' },
        { key: 'shortExcess', label: 'Short (-) / Excess (+)', category: 'Stock' },
        { key: 'storageLocations', label: 'Storage Locations', category: 'Location' },
        { key: 'dispatchStatus', label: 'Dispatch Status', category: 'Basic' },
        { key: 'createdAt', label: 'Date Created', category: 'Basic' },
        { key: 'createdBy', label: 'Created By', category: 'Basic' },
        { key: 'approvedBy', label: 'Approved By', category: 'Basic' },
        { key: 'approvedAt', label: 'Approved At', category: 'Basic' },
        { key: 'remarks', label: 'Gate Entry Remarks', category: 'Basic' },
        { key: 'dispatchRemarks', label: 'Dispatch Remarks', category: 'Basic' },
        { key: 'destination', label: 'Destination', category: 'Location' },
        { key: 'vehicleNumber', label: 'Vehicle Number', category: 'Transport' },
        { key: 'driverName', label: 'Driver Name', category: 'Transport' },
        { key: 'driverPhone', label: 'Driver Phone', category: 'Transport' },
        { key: 'dispatchVehicleNo', label: 'Dispatch Vehicle No', category: 'Transport' },
        { key: 'dispatchTransporterName', label: 'Dispatch Transporter Name', category: 'Transport' },
        { key: 'lrNumber', label: 'LR Number', category: 'Transport' },
        { key: 'dispatchLrDate', label: 'Dispatch LR Date', category: 'Transport' },
        { key: 'invoiceNumber', label: 'Invoice Number', category: 'Documents' },
        { key: 'invDate', label: 'Invoice Date', category: 'Documents' },
        { key: 'ewayBillNumber', label: 'E-Way Bill Number', category: 'Documents' },
        { key: 'eWaybillNumber', label: 'E-Waybill Number (Dispatch)', category: 'Documents' },
        { key: 'packingSlipNumber', label: 'Packing Slip Number', category: 'Documents' },
        { key: 'expectedQuantity', label: 'Expected Quantity', category: 'Documents' },
        { key: 'dockNumber', label: 'Dock Number', category: 'Basic' }
      ],
      mis_inward: [
        { key: 'mis_gate_entry_no', label: 'Security Entry No.', category: 'Entry' },
        { key: 'mis_client_name', label: 'Customer Name', category: 'Entry' },
        { key: 'mis_grn_no', label: 'GRN No.', category: 'Entry' },
        { key: 'mis_supplier_name', label: 'Supplier Name', category: 'Entry' },
        { key: 'mis_supplier_location', label: 'Supplier Location', category: 'Entry' },
        { key: 'mis_date_receipt', label: 'Date Of Receipt', category: 'Entry' },
        { key: 'mis_in_time', label: 'In Time', category: 'Entry' },
        { key: 'mis_vehicle_no', label: 'Vehicle No', category: 'Transport' },
        { key: 'mis_vehicle_type', label: 'Vehicle Type', category: 'Transport' },
        { key: 'mis_transporter', label: 'Transporter Name', category: 'Transport' },
        { key: 'mis_lr_no', label: 'LR No', category: 'Transport' },
        { key: 'mis_inv_no', label: 'Invoice No', category: 'Documents' },
        { key: 'mis_inv_date', label: 'Invoice Date', category: 'Documents' },
        { key: 'mis_item_code', label: 'Item Code', category: 'Product' },
        { key: 'mis_item_desc', label: 'Item Description', category: 'Product' },
        { key: 'mis_pack_size', label: 'Pack Size', category: 'Product' },
        { key: 'mis_uom', label: 'UOM', category: 'Product' },
        { key: 'mis_master_packing', label: 'Master Packing', category: 'Product' },
        { key: 'mis_lot_no', label: 'Lot No.', category: 'Product' },
        { key: 'mis_batch_no', label: 'Batch Number', category: 'Product' },
        { key: 'mis_mfg_date', label: 'MFG Date', category: 'Product' },
        { key: 'mis_exp_date', label: 'Expiry Date', category: 'Product' },
        { key: 'mis_inv_qty', label: 'Invoice Qty', category: 'Stock' },
        { key: 'mis_rec_qty', label: 'Received Qty', category: 'Stock' },
        { key: 'mis_short_excess', label: 'Short / Excess', category: 'Stock' },
        { key: 'mis_good', label: 'Good Qty', category: 'Stock' },
        { key: 'mis_damage', label: 'Damage / Leakage', category: 'Stock' },
        { key: 'mis_wt_per_uom', label: 'Wt per UOM (kg)', category: 'Stock' },
        { key: 'mis_total_weight', label: 'Total Weight (kg)', category: 'Stock' },
        { key: 'mis_grand_total', label: 'Grand Total', category: 'Stock' },
        { key: 'mis_handling_type', label: 'Handling Type', category: 'Other' },
        { key: 'mis_remark', label: 'Remark', category: 'Other' },
      ],
      mis_outward: [
        { key: 'mis_out_gate_entry_no', label: 'Security Entry No.', category: 'Entry' },
        { key: 'mis_out_client_name', label: 'Customer Name', category: 'Entry' },
        { key: 'mis_out_gon_no', label: 'GON No.', category: 'Entry' },
        { key: 'mis_out_consignee', label: 'Consignee Name', category: 'Entry' },
        { key: 'mis_out_consignee_loc', label: 'Consignee Location', category: 'Entry' },
        { key: 'mis_out_date', label: 'Date Of Dispatch', category: 'Entry' },
        { key: 'mis_out_time', label: 'Out Time', category: 'Entry' },
        { key: 'mis_out_vehicle_no', label: 'Vehicle No', category: 'Transport' },
        { key: 'mis_out_vehicle_type', label: 'Vehicle Type', category: 'Transport' },
        { key: 'mis_out_transporter', label: 'Transporter Name', category: 'Transport' },
        { key: 'mis_out_lr_no', label: 'LR No', category: 'Transport' },
        { key: 'mis_out_inv_no', label: 'Invoice No', category: 'Documents' },
        { key: 'mis_out_inv_date', label: 'Invoice Date', category: 'Documents' },
        { key: 'mis_out_item_code', label: 'Item Code', category: 'Product' },
        { key: 'mis_out_item_desc', label: 'Item Description', category: 'Product' },
        { key: 'mis_out_pack_size', label: 'Pack Size', category: 'Product' },
        { key: 'mis_out_uom', label: 'UOM', category: 'Product' },
        { key: 'mis_out_master_packing', label: 'Master Packing', category: 'Product' },
        { key: 'mis_out_lot_no', label: 'Lot No.', category: 'Product' },
        { key: 'mis_out_batch_no', label: 'Batch Number', category: 'Product' },
        { key: 'mis_out_mfg_date', label: 'MFG Date', category: 'Product' },
        { key: 'mis_out_exp_date', label: 'Expiry Date', category: 'Product' },
        { key: 'mis_out_inv_qty', label: 'Invoice Qty', category: 'Stock' },
        { key: 'mis_out_disp_qty', label: 'Dispatched Qty', category: 'Stock' },
        { key: 'mis_out_short_excess', label: 'Short / Excess', category: 'Stock' },
        { key: 'mis_out_wt_per_uom', label: 'Wt per UOM (kg)', category: 'Stock' },
        { key: 'mis_out_total_weight', label: 'Total Weight (kg)', category: 'Stock' },
        { key: 'mis_out_grand_total', label: 'Grand Total', category: 'Stock' },
        { key: 'mis_out_handling_type', label: 'Handling Type', category: 'Other' },
        { key: 'mis_out_remark', label: 'Remark', category: 'Other' },
      ],
      mis_stock_summary: [
        { key: 'mis_ss_client', label: 'Customer Name', category: 'Basic' },
        { key: 'mis_ss_item_code', label: 'Item Code', category: 'Basic' },
        { key: 'mis_ss_item_desc', label: 'Item Description', category: 'Basic' },
        { key: 'mis_ss_pack_size', label: 'Pack Size', category: 'Basic' },
        { key: 'mis_ss_uom', label: 'UOM', category: 'Basic' },
        { key: 'mis_ss_master_packing', label: 'Master Packing', category: 'Basic' },
        { key: 'mis_ss_lot_no', label: 'Lot No.', category: 'Basic' },
        { key: 'mis_ss_batch_no', label: 'Batch Number', category: 'Basic' },
        { key: 'mis_ss_mfg_date', label: 'MFG Date', category: 'Basic' },
        { key: 'mis_ss_exp_date', label: 'Expiry Date', category: 'Basic' },
        { key: 'mis_ss_wt_per_uom', label: 'Wt per UOM (kg)', category: 'Basic' },
        { key: 'mis_ss_opp_good', label: 'Opening Good', category: 'Opening Stock' },
        { key: 'mis_ss_opp_dam', label: 'Opening Damage', category: 'Opening Stock' },
        { key: 'mis_ss_in_good', label: 'Inbound Good', category: 'Inbound' },
        { key: 'mis_ss_in_dam', label: 'Inbound Damage', category: 'Inbound' },
        { key: 'mis_ss_out_good', label: 'Outbound Good', category: 'Outbound' },
        { key: 'mis_ss_out_dam', label: 'Outbound Damage', category: 'Outbound' },
        { key: 'mis_ss_clo_good', label: 'Closing Good', category: 'Closing Stock' },
        { key: 'mis_ss_clo_dam', label: 'Closing Damage', category: 'Closing Stock' },
        { key: 'mis_ss_clo_kg', label: 'Closing in KG', category: 'Closing Stock' },
        { key: 'mis_ss_grand_total', label: 'Grand Total', category: 'Closing Stock' },
        { key: 'mis_ss_remark', label: 'Remark', category: 'Other' },
      ],
      mis_opening_stock: [
        { key: 'os_client', label: 'Customer Name', category: 'Basic' },
        { key: 'os_date', label: 'Date', category: 'Basic' },
        { key: 'os_item_code', label: 'Item Code', category: 'Product' },
        { key: 'os_item_desc', label: 'Description', category: 'Product' },
        { key: 'os_pack_size', label: 'Pack Size', category: 'Product' },
        { key: 'os_uom', label: 'UOM', category: 'Product' },
        { key: 'os_master_packing', label: 'Master Packing', category: 'Product' },
        { key: 'os_lot_no', label: 'Lot No.', category: 'Product' },
        { key: 'os_batch_no', label: 'Batch Number', category: 'Product' },
        { key: 'os_mfg_date', label: 'Mfg Date', category: 'Product' },
        { key: 'os_exp_date', label: 'Ex Date', category: 'Product' },
        { key: 'os_qty', label: 'Qty', category: 'Stock' },
        { key: 'os_good', label: 'Good', category: 'Stock' },
        { key: 'os_damage', label: 'Damage / Leakage', category: 'Stock' },
        { key: 'os_wt_per_uom', label: 'Net Wt per UOM in Kg.', category: 'Stock' },
        { key: 'os_total_weight', label: 'Total Weight in KG', category: 'Stock' },
        { key: 'os_grand_total', label: 'Grand Total', category: 'Stock' },
        { key: 'os_remark', label: 'Remark', category: 'Other' },
      ],
      damage: [
        { key: 'grnNumber', label: 'GRN Number', category: 'Basic' },
        { key: 'grnStatus', label: 'GRN Status', category: 'Basic' },
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'productDescription', label: 'Product Description', category: 'Product' },
        { key: 'batchNo', label: 'Batch Number', category: 'Product' },
        { key: 'damQty', label: 'DAM Quantity', category: 'Stock' },
        { key: 'cdamQty', label: 'CDAM Quantity', category: 'Stock' },
        { key: 'totalDamaged', label: 'Total Damaged', category: 'Stock' },
        { key: 'grandTotal', label: 'Grand Total', category: 'Stock' },
        { key: 'lbdStorageMode', label: 'LBD Type', category: 'LBD' },
        { key: 'lbdZoneCodes', label: 'LBD Zone / Pallet', category: 'LBD' },
        { key: 'lbdPalletCount', label: 'LBD Pallet Count', category: 'LBD' },
        { key: 'lbdSharedPalletRef', label: 'Shared Pallet Ref', category: 'LBD' },
        { key: 'lbdRemark', label: 'LBD Remark', category: 'LBD' },
        { key: 'createdAt', label: 'Date Created', category: 'Basic' },
        { key: 'updatedAt', label: 'Last Updated', category: 'Basic' }
      ],
      billing: [
        { key: 'invoiceNumber', label: 'Invoice Number', category: 'Basic' },
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'warehouseName', label: 'Warehouse', category: 'Basic' },
        { key: 'invoiceStatus', label: 'Invoice Status', category: 'Basic' },
        { key: 'billingPeriodStart', label: 'Billing Period Start', category: 'Basic' },
        { key: 'billingPeriodEnd', label: 'Billing Period End', category: 'Basic' },
        { key: 'chargeType', label: 'Charge Type', category: 'Charge' },
        { key: 'description', label: 'Description', category: 'Charge' },
        { key: 'quantity', label: 'Quantity', category: 'Charge' },
        { key: 'unit', label: 'Unit', category: 'Charge' },
        { key: 'rate', label: 'Rate', category: 'Charge' },
        { key: 'amount', label: 'Amount', category: 'Charge' },
        { key: 'referenceType', label: 'Reference Type', category: 'Charge' },
        { key: 'referenceId', label: 'Reference ID', category: 'Charge' },
        { key: 'invoiceSubtotal', label: 'Invoice Subtotal', category: 'Invoice' },
        { key: 'invoiceCgstAmount', label: 'Invoice CGST Amount', category: 'Invoice' },
        { key: 'invoiceSgstAmount', label: 'Invoice SGST Amount', category: 'Invoice' },
        { key: 'invoiceRoundOff', label: 'Invoice Round Off', category: 'Invoice' },
        { key: 'invoiceTotalAmount', label: 'Invoice Total Amount', category: 'Invoice' },
        { key: 'invoiceCreatedAt', label: 'Invoice Date', category: 'Invoice' },
        { key: 'createdAt', label: 'Line Item Date', category: 'Basic' }
      ],
      'client-utilization': [
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'clientType', label: 'Client Type', category: 'Basic' },
        { key: 'palletsAllocated', label: 'Base Allocated', category: 'Stock' },
        { key: 'palletsUsed', label: 'Base Used', category: 'Stock' },
        { key: 'palletsRemaining', label: 'Base Remaining', category: 'Stock' },
        { key: 'additionalSpaceSqft', label: 'Additional Space', category: 'Stock' },
        { key: 'additionalPalletsUsed', label: 'Additional Pallets', category: 'Stock' },
        { key: 'utilization', label: 'Utilization %', category: 'Stock' }
      ],
      'additional-space': [
        { key: 'clientName', label: 'Client', category: 'Basic' },
        { key: 'clientType', label: 'Client Type', category: 'Basic' },
        { key: 'spaceSqft', label: 'Space/Pallets', category: 'Stock' },
        { key: 'startDate', label: 'Start Date', category: 'Basic' },
        { key: 'endDate', label: 'End Date', category: 'Basic' },
        { key: 'durationDays', label: 'Duration (Days)', category: 'Basic' },
        { key: 'status', label: 'Status', category: 'Basic' },
        { key: 'grnNumber', label: 'GRN Number', category: 'Basic' }
      ]
    };

    return fieldsByType[baseType] || fieldsByType[type] || [];
  };

  // Default selected fields for each report type (key fields only)
  const getDefaultSelectedFields = (type: string) => {
    const baseType = type.endsWith('_grid') ? type.slice(0, -5) : type;
    const defaults: Record<string, string[]> = {
      inventory: ['clientName', 'productDescription', 'batchNo', 'totalQty', 'grandTotal', 'palletCount'],
      inventory_grid: ['clientName', 'productDescription', 'batchNo', 'goodQty', 'cdamQty', 'damQty', 'totalQty', 'grandTotal', 'palletCount'],
      inward: ['grnNumber', 'clientName', 'productDescription', 'goodQty', 'grandTotal', 'createdAt'],
      outward: ['dispatchNumber', 'clientName', 'productDescription', 'goodQty', 'cdamQty', 'damQty', 'grandTotal', 'palletCount', 'createdAt'],
      damage: ['clientName', 'productDescription', 'totalDamaged', 'grandTotal', 'lbdZoneCodes', 'updatedAt'],
      billing: ['invoiceNumber', 'clientName', 'chargeType', 'description', 'amount', 'invoiceStatus'],
      'client-utilization': ['clientName', 'clientType', 'palletsUsed', 'utilization'],
      'additional-space': ['clientName', 'spaceSqft', 'startDate', 'status'],
      mis_inward: [
        'mis_gate_entry_no','mis_client_name','mis_grn_no','mis_supplier_name','mis_supplier_location',
        'mis_date_receipt','mis_in_time','mis_vehicle_no','mis_vehicle_type','mis_transporter','mis_lr_no',
        'mis_inv_no','mis_inv_date','mis_item_code','mis_item_desc','mis_pack_size','mis_uom',
        'mis_master_packing','mis_lot_no','mis_batch_no','mis_mfg_date','mis_exp_date',
        'mis_inv_qty','mis_rec_qty','mis_short_excess','mis_good','mis_damage',
        'mis_wt_per_uom','mis_total_weight','mis_grand_total','mis_handling_type','mis_remark',
      ],
      mis_outward: [
        'mis_out_gate_entry_no','mis_out_client_name','mis_out_gon_no','mis_out_consignee','mis_out_consignee_loc',
        'mis_out_date','mis_out_time','mis_out_vehicle_no','mis_out_vehicle_type','mis_out_transporter','mis_out_lr_no',
        'mis_out_inv_no','mis_out_inv_date','mis_out_item_code','mis_out_item_desc','mis_out_pack_size','mis_out_uom',
        'mis_out_master_packing','mis_out_lot_no','mis_out_batch_no','mis_out_mfg_date','mis_out_exp_date',
        'mis_out_inv_qty','mis_out_disp_qty','mis_out_short_excess',
        'mis_out_wt_per_uom','mis_out_total_weight','mis_out_grand_total','mis_out_handling_type','mis_out_remark',
      ],
      mis_stock_summary: [
        'mis_ss_client','mis_ss_item_code','mis_ss_item_desc','mis_ss_pack_size','mis_ss_uom',
        'mis_ss_master_packing','mis_ss_lot_no','mis_ss_batch_no','mis_ss_mfg_date','mis_ss_exp_date',
        'mis_ss_wt_per_uom','mis_ss_opp_good','mis_ss_opp_dam','mis_ss_in_good','mis_ss_in_dam',
        'mis_ss_out_good','mis_ss_out_dam','mis_ss_clo_good','mis_ss_clo_dam','mis_ss_clo_kg','mis_ss_grand_total','mis_ss_remark',
      ],
      mis_opening_stock: [
        'os_client','os_date','os_item_code','os_item_desc','os_pack_size','os_uom','os_master_packing',
        'os_lot_no','os_batch_no','os_mfg_date','os_exp_date','os_qty','os_good','os_damage',
        'os_wt_per_uom','os_total_weight','os_grand_total','os_remark',
      ],
    };
    return defaults[baseType] || defaults[type] || ['clientName'];
  };

  const [selectedFields, setSelectedFields] = useState<string[]>(() =>
    getDefaultSelectedFields(reportType)
  );

  const queryClient = useQueryClient();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    clientId: null as number | null,
    warehouseId: null as number | null,
    recipientEmail: '',
    scheduleTime: '18:00',
    timezone: 'Asia/Kolkata',
    isActive: true,
  });
  const [emailError, setEmailError] = useState('');

  const validateEmails = (raw: string): string => {
    const emails = raw.split(',').map(e => e.trim()).filter(Boolean);
    if (!emails.length) return 'At least one email address is required.';
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const invalid = emails.filter(e => !re.test(e));
    if (invalid.length === 1) return `"${invalid[0]}" is not a valid email address.`;
    if (invalid.length > 1) return `${invalid.length} invalid addresses: ${invalid.join(', ')}`;
    return '';
  };

  // Config context: which client + warehouse + sub-sheet is being configured
  const [configClientId, setConfigClientId] = useState<number | null>(null);
  const [configWarehouseId, setConfigWarehouseId] = useState<number | null>(null);
  const [configSheetType, setConfigSheetType] = useState<'inventory' | 'inward' | 'outward' | 'inventory_grid' | 'mis_inward' | 'mis_outward' | 'mis_stock_summary' | 'mis_opening_stock'>('inventory_grid');
  // Fields for the config panel (separate from table-display selectedFields)
  const [configPanelFields, setConfigPanelFields] = useState<string[]>(() =>
    getDefaultSelectedFields('inventory_grid')
  );

  // Fetch clients and warehouses for config dropdowns
  const { data: clientsList } = useQuery({
    queryKey: ['clients-list-for-config'],
    queryFn: async () => {
      const res = await api.get('/clients?limit=100&order=ASC&sort=companyName');
      return (res?.data?.items || []) as Array<{ id: number; companyName: string }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: warehousesList } = useQuery({
    queryKey: ['warehouses-list-for-config'],
    queryFn: async () => {
      const res = await api.get('/warehouses?limit=100&order=ASC&sort=name');
      return (res?.data?.items || []) as Array<{ id: number; name: string }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Auto-select first warehouse on inventory page load so inventory_grid config is fetched immediately
  useEffect(() => {
    if (warehousesList?.length && configWarehouseId === null && reportType === 'inventory') {
      setConfigWarehouseId(warehousesList[0].id);
    }
  }, [warehousesList, reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load saved config for the selected client + warehouse + sub-sheet
  const { data: savedConfig, isLoading: configLoading } = useQuery({
    queryKey: ['report-config', configSheetType, configClientId, configWarehouseId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (configClientId) params.append('clientId', configClientId.toString());
      if (configWarehouseId) params.append('warehouseId', configWarehouseId.toString());
      const response = await api.get(`/report-config/${configSheetType}?${params.toString()}`);
      return response?.data ?? null;
    },
    enabled: configWarehouseId !== null && (configSheetType === 'inventory_grid' || configClientId !== null),
    staleTime: Infinity,
  });

  // Load saved config into panel fields when selection changes
  useEffect(() => {
    const canLoad = configSheetType === 'inventory_grid'
      ? configWarehouseId !== null
      : configClientId !== null && configWarehouseId !== null;
    if (canLoad) {
      if (savedConfig?.selectedFields?.length) {
        setConfigPanelFields(savedConfig.selectedFields);
        if (configSheetType === 'inventory_grid') setSelectedFields(savedConfig.selectedFields);
      } else if (savedConfig === null) {
        const defaults = getDefaultSelectedFields(configSheetType);
        setConfigPanelFields(defaults);
        if (configSheetType === 'inventory_grid') setSelectedFields(defaults);
      }
    }
  }, [savedConfig, configClientId, configWarehouseId, configSheetType]);

  // Mutation to persist config — only saves when both client + warehouse are chosen
  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: (fields: string[]) =>
      api.put(`/report-config/${configSheetType}`, {
        selectedFields: fields,
        clientId: configClientId,
        warehouseId: configWarehouseId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['report-config', configSheetType, configClientId, configWarehouseId] });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to save report configuration');
    },
  });

  // Debounced save — inventory_grid only needs warehouse; other types need both client + warehouse
  const scheduleSave = useCallback((fields: string[]) => {
    if (configSheetType === 'inventory_grid') {
      if (!configWarehouseId) return;
    } else {
      if (!configClientId || !configWarehouseId) return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => saveConfig(fields), 800);
  }, [saveConfig, configClientId, configWarehouseId, configSheetType]);

  // ── Non-inventory grid config — global per report type (no client/warehouse) ─
  const { data: nonInvGridConfig } = useQuery({
    queryKey: ['grid-config', reportType],
    queryFn: async () => {
      const res = await api.get(`/report-config/${reportType}_grid`);
      return res?.data ?? null;
    },
    enabled: reportType !== 'inventory',
    staleTime: Infinity,
  });

  // Apply non-inventory grid config to selectedFields whenever reportType or config changes
  useEffect(() => {
    if (reportType === 'inventory') return;
    if (nonInvGridConfig?.selectedFields?.length) {
      setSelectedFields(nonInvGridConfig.selectedFields);
    } else if (nonInvGridConfig === null) {
      setSelectedFields(getDefaultSelectedFields(reportType));
    }
  }, [nonInvGridConfig, reportType]);

  const nonInvGridSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: saveNonInvGridConfig, isPending: isNonInvGridSaving } = useMutation({
    mutationFn: (fields: string[]) =>
      api.put(`/report-config/${reportType}_grid`, {
        selectedFields: fields,
        clientId: null,
        warehouseId: null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grid-config', reportType] });
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to save grid configuration');
    },
  });

  const scheduleNonInvGridSave = useCallback((fields: string[]) => {
    if (nonInvGridSaveTimerRef.current) clearTimeout(nonInvGridSaveTimerRef.current);
    nonInvGridSaveTimerRef.current = setTimeout(() => saveNonInvGridConfig(fields), 800);
  }, [saveNonInvGridConfig]);
  // ────────────────────────────────────────────────────────────────────────────

  // ── Schedule queries / mutations ───────────────────────────────────────────
  const { data: scheduleData, refetch: refetchSchedule } = useQuery({
    queryKey: ['scheduled-report', scheduleForm.clientId, scheduleForm.warehouseId],
    queryFn: async () => {
      if (!scheduleForm.clientId) return null;
      const params = new URLSearchParams();
      params.append('clientId', scheduleForm.clientId.toString());
      if (scheduleForm.warehouseId) params.append('warehouseId', scheduleForm.warehouseId.toString());
      const res = await api.get(`/scheduled-reports/client?${params.toString()}`);
      return res?.data ?? null;
    },
    enabled: showScheduleModal && scheduleForm.clientId !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (scheduleData) {
      setScheduleForm(prev => ({
        ...prev,
        recipientEmail: scheduleData.recipientEmail ?? prev.recipientEmail,
        scheduleTime:   scheduleData.scheduleTime   ?? prev.scheduleTime,
        timezone:       scheduleData.timezone       ?? prev.timezone,
        isActive:       scheduleData.isActive       ?? prev.isActive,
      }));
    }
  }, [scheduleData]);

  const { mutate: saveSchedule, isPending: isSavingSchedule } = useMutation({
    mutationFn: () =>
      api.put('/scheduled-reports', {
        clientId:       scheduleForm.clientId,
        warehouseId:    scheduleForm.warehouseId,
        recipientEmail: scheduleForm.recipientEmail,
        scheduleTime:   scheduleForm.scheduleTime,
        timezone:       scheduleForm.timezone,
        isActive:       scheduleForm.isActive,
      }),
    onSuccess: () => {
      refetchSchedule();
      toast.success('Schedule saved — report will be emailed at the configured time.');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to save schedule');
    },
  });

  const { mutate: deleteSchedule, isPending: isDeletingSchedule } = useMutation({
    mutationFn: () => api.delete(`/scheduled-reports/${scheduleData?.id}`),
    onSuccess: () => {
      refetchSchedule();
      setScheduleForm(prev => ({ ...prev, recipientEmail: '', isActive: true }));
      toast.success('Schedule removed.');
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Failed to remove schedule');
    },
  });
  // ────────────────────────────────────────────────────────────────────────────

  // Update selected fields when report type changes
  const handleReportTypeChange = useCallback((newType: ReportType) => {
    setReportType(newType);
    setFilters({}); // Clear filters when changing report type
    // selectedFields will be set by the savedConfig effect once the query loads
    setSelectedFields(getDefaultSelectedFields(newType));
  }, []);

  // Field selection functions
  const handleFieldToggle = useCallback((fieldKey: string) => {
    setSelectedFields(prev => {
      const next = prev.includes(fieldKey)
        ? prev.filter(key => key !== fieldKey)
        : [...prev, fieldKey];
      if (reportType !== 'inventory') scheduleNonInvGridSave(next);
      else scheduleSave(next);
      return next;
    });
  }, [scheduleSave, scheduleNonInvGridSave, reportType]);

  const handleSelectAllFields = useCallback(() => {
    const fields = getAvailableFields(reportType).map(f => f.key);
    setSelectedFields(fields);
    if (reportType !== 'inventory') scheduleNonInvGridSave(fields);
    else scheduleSave(fields);
  }, [reportType, scheduleSave, scheduleNonInvGridSave]);

  const handleDeselectAllFields = useCallback(() => {
    setSelectedFields([]);
    if (reportType !== 'inventory') scheduleNonInvGridSave([]);
    else scheduleSave([]);
  }, [scheduleSave, scheduleNonInvGridSave, reportType]);

  // Config panel handlers — operate on configPanelFields (not table selectedFields)
  // When configSheetType is 'inventory_grid', also sync selectedFields so the table updates live
  const handleConfigFieldToggle = useCallback((fieldKey: string) => {
    setConfigPanelFields(prev => {
      const next = prev.includes(fieldKey)
        ? prev.filter(k => k !== fieldKey)
        : [...prev, fieldKey];
      scheduleSave(next);
      if (configSheetType === 'inventory_grid') setSelectedFields(next);
      return next;
    });
  }, [scheduleSave, configSheetType]);

  const handleConfigSelectAll = useCallback(() => {
    const fields = getAvailableFields(configSheetType).map(f => f.key);
    setConfigPanelFields(fields);
    scheduleSave(fields);
    if (configSheetType === 'inventory_grid') setSelectedFields(fields);
  }, [configSheetType, scheduleSave]);

  const handleConfigDeselectAll = useCallback(() => {
    setConfigPanelFields([]);
    scheduleSave([]);
    if (configSheetType === 'inventory_grid') setSelectedFields([]);
  }, [scheduleSave, configSheetType]);

  // Get current available fields (for table display)
  const availableFields = useMemo(() => getAvailableFields(reportType), [reportType]);

  const fieldsByCategory = useMemo(() => {
    return availableFields.reduce((acc, field) => {
      if (!acc[field.category]) acc[field.category] = [];
      acc[field.category].push(field);
      return acc;
    }, {} as Record<string, typeof availableFields>);
  }, [availableFields]);

  // Config panel available fields & categories (driven by configSheetType)
  const configPanelAvailableFields = useMemo(() => getAvailableFields(configSheetType), [configSheetType]);
  const configPanelFieldsByCategory = useMemo(() => {
    return configPanelAvailableFields.reduce((acc, field) => {
      if (!acc[field.category]) acc[field.category] = [];
      acc[field.category].push(field);
      return acc;
    }, {} as Record<string, typeof configPanelAvailableFields>);
  }, [configPanelAvailableFields]);

  // Separate filters for summary (without search) - KPIs and Charts don't need search
  const summaryFilters = useMemo(() => {
    const { search, ...rest } = filters;
    return rest;
  }, [filters]);

  // Fetch summary data (for KPIs and charts - doesn't include search)
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['reports', reportType, 'summary', summaryFilters],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (summaryFilters.clientId) params.append('clientId', summaryFilters.clientId.toString());
        if (summaryFilters.dateFrom) params.append('dateFrom', summaryFilters.dateFrom);
        if (summaryFilters.dateTo) params.append('dateTo', summaryFilters.dateTo);
        if (summaryFilters.productDescription) params.append('productDescription', summaryFilters.productDescription);
        if (summaryFilters.batchNo) params.append('batchNo', summaryFilters.batchNo);
        if (summaryFilters.status) params.append('status', summaryFilters.status);
        
        const response = await api.get(`/reports/${reportType}/summary?${params}`);
        
        // The response interceptor returns response.data directly
        // Backend responds with { message: string, data: actualData }
        if (!response || typeof response !== 'object') {
          console.error('Invalid summary response structure:', response);
          return null;
        }

        return response.data || null;
      } catch (error) {
        console.error('Error fetching summary data:', error);
        return null;
      }
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });

  // Fetch paginated table data
  const { data: reportData, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['reports', reportType, 'table', filters],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const params = new URLSearchParams();
        params.append('page', pageParam.toString());
        params.append('limit', limit.toString());
        if (filters.clientId) params.append('clientId', filters.clientId.toString());
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.productDescription) params.append('productDescription', filters.productDescription);
        if (filters.batchNo) params.append('batchNo', filters.batchNo);
        if (filters.search) params.append('search', filters.search);
        if (filters.status) params.append('status', filters.status);
        
        const response = await api.get(`/reports/${reportType}?${params}`);
        
        // The response interceptor returns response.data directly
        // Backend responds with { message: string, data: { items: [], meta: {} } }
        if (!response || typeof response !== 'object') {
          console.error('Invalid response structure:', response);
          return { items: [], meta: { page: 1, limit, total: 0, totalPages: 0 } };
        }

        const data = response.data;
        
        // Ensure the data has the expected structure
        if (!data || typeof data !== 'object' || !Array.isArray(data.items)) {
          console.error('Invalid data structure - expected items array:', data);
          return { items: [], meta: { page: 1, limit, total: 0, totalPages: 0 } };
        }
        
        return data;
      } catch (error) {
        console.error('Error fetching report data:', error);
        // Return empty structure on error to prevent crashes
        return { items: [], meta: { page: 1, limit, total: 0, totalPages: 0 } };
      }
    },
    getNextPageParam: (lastPage) => {
      // Safely check if lastPage exists and has the expected meta structure
      if (lastPage && lastPage.meta && typeof lastPage.meta === 'object') {
        const { page, totalPages } = lastPage.meta;
        if (typeof page === 'number' && typeof totalPages === 'number' && page < totalPages) {
          return page + 1;
        }
      }
      return undefined;
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
  });

  // Flatten paginated data with safe access - memoized to prevent re-creation on every render
  const allItems = useMemo(() => {
    return reportData?.pages?.flatMap(page => page?.items || []) || [];
  }, [reportData?.pages]);
  
  const totalRecords = reportData?.pages?.[0]?.meta?.total || 0;

  const handleFilterChange = useCallback((newFilters: Partial<ReportFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setSearchTerm('');
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRowClick = useCallback((row: any) => {
    setSelectedRow(row);
    setShowDetailDrawer(true);
  }, []);

  // Stable search change handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Stable search trigger handler (debounced by SearchBar)
  const handleSearch = useCallback((value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
  }, []);

  const handleImportClick = useCallback(() => {
    const requiredPermission = reportType === 'outward' ? 'dispatch:import' : 'grn:import';
    if (!hasPermission(requiredPermission)) {
      return;
    }
    importFileInputRef.current?.click();
  }, [hasPermission, reportType]);

  const handleImportFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    const isOutbound = reportType === 'outward';
    const label = isOutbound ? 'Outbound' : 'Inbound';

    try {
      setIsImporting(true);
      const summary = isOutbound
        ? await outwardImportService.importExcel(file)
        : await inwardImportService.importExcel(file);

      console.log(`[${label} Import] Full summary:`, summary);
      console.table(summary.results as unknown as Array<Record<string, unknown>>);

      if (summary.totalRows === 0) {
        toast.error(`${label} import: 0 rows detected. Check that your Excel headers match the expected names exactly (row 1 must be headers).`);
      } else if (summary.failed === 0) {
        toast.success(`${label}: imported ${summary.succeeded} of ${summary.totalRows} rows.`);
      } else {
        const firstFailure = (summary.results as Array<{ rowIndex: number; status: string; reason?: string }>).find((r) => r.status === 'failed');
        const reason = firstFailure ? ` First error (row ${firstFailure.rowIndex}): ${firstFailure.reason}` : '';
        toast.error(`${label}: ${summary.succeeded} of ${summary.totalRows} succeeded, ${summary.failed} failed.${reason}`);
      }

      await queryClient.invalidateQueries({ queryKey: ['reports'] });
    } catch (error: any) {
      console.error(`${label} import failed:`, error);
      const message = error?.response?.data?.message || error?.message || 'Import failed. Please try again.';
      toast.error(message);
    } finally {
      setIsImporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, reportType]);

  // Export to Excel with selected fields only
  const handleExportExcel = useCallback(async () => {
    if (!hasPermission('reports:export')) {
      return;
    }

    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export.');
      return;
    }

    try {
      setIsExporting(true);

      // Get all data from backend first, then filter on client side
      const params = new URLSearchParams();
      if (filters.clientId) params.append('clientId', filters.clientId.toString());
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (filters.productDescription) params.append('productDescription', filters.productDescription);
      if (filters.batchNo) params.append('batchNo', filters.batchNo);
      if (filters.warehouseId) params.append('warehouseId', filters.warehouseId.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      // Get ALL filtered rows from backend. The list endpoint caps limit at
      // 100, so page through every page instead of exporting only page 1.
      const PAGE_SIZE = 100;
      params.set('limit', PAGE_SIZE.toString());
      params.set('page', '1');
      const firstResp = await api.get(`/reports/${reportType}?${params.toString()}`);
      const allData: any[] = firstResp?.data?.items || [];
      const totalPages: number = firstResp?.data?.meta?.totalPages || 1;
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => {
            const p = new URLSearchParams(params);
            p.set('page', String(i + 2));
            return api.get(`/reports/${reportType}?${p.toString()}`);
          })
        );
        rest.forEach(r => allData.push(...(r?.data?.items || [])));
      }

      if (allData.length === 0) {
        toast.warning('No data available to export with current filters.');
        return;
      }

      // Inventory export: split each item into one row per rack/zone location
      // it's actually stored in, using that location's live stored quantity
      // instead of the item's aggregated total (client-requested — a single
      // "Storage" cell like "AA-06 | AA-07" hides which location holds how much).
      const grandTotalOf = (masterPacking: any, packSize: any, qty: number) =>
        (parseFloat(String(masterPacking)) || 0) * (parseFloat(String(packSize)) || 0) * (qty || 0);

      const exportRows = reportType !== 'inventory' ? allData : allData.flatMap((item: any) => {
        const breakdown: Array<{ code: string; qty: number }> = String(item.locationBreakdown || '')
          .split('|')
          .filter(Boolean)
          .map((entry: string) => {
            const sep = entry.lastIndexOf(':');
            return { code: entry.slice(0, sep), qty: parseInt(entry.slice(sep + 1), 10) || 0 };
          });

        if (breakdown.length === 0) {
          return [{ ...item, grandTotal: grandTotalOf(item.masterPacking, item.packSize, item.totalQty) }];
        }

        return breakdown.map(({ code, qty }) => ({
          ...item,
          storageLocations: code,
          totalQty: qty,
          goodQty: undefined,
          damQty: undefined,
          cdamQty: undefined,
          totalWeight: item.weightPerItem ? (parseFloat(String(item.weightPerItem)) || 0) * qty : item.totalWeight,
          grandTotal: grandTotalOf(item.masterPacking, item.packSize, qty),
        }));
      });

      // Filter data to only include selected fields
      const filteredData = exportRows.map((item: any) => {
        const filteredItem: any = {};
        selectedFields.forEach(field => {
          if (item.hasOwnProperty(field)) {
            filteredItem[field] = item[field];
          }
        });
        return filteredItem;
      });

      // Derive the client subtitle from the unfiltered rows (the client column
      // may not be among the selected export fields).
      const subtitle = filters.clientId ? (allData[0]?.clientName || 'Client') : 'All Clients';

      // Create styled Excel file using client-side library
      await downloadExcelFile(filteredData, selectedFields, reportType, subtitle);

      toast.success(`Excel export completed with ${selectedFields.length} selected fields (${allData.length} records).`);
    } catch (error: any) {
      console.error('Excel export failed:', error);
      
      // Try to extract error message from blob response
      let errorMessage = 'Failed to export Excel file. Please try again.';
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // Fallback to default message
        }
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission, filters, reportType, selectedFields, availableFields]);

  // Client-side styled Excel generation — matches the Client Report design
  // (orange title bar -> client subtitle -> green headers -> zebra rows).
  const downloadExcelFile = async (data: any[], fields: string[], reportType: string, subtitle: string) => {
    const fieldLabels = availableFields.reduce((acc, field) => {
      acc[field.key] = field.label;
      return acc;
    }, {} as Record<string, string>);

    const dateFields = new Set(['createdAt', 'mfgDate', 'expiryDate', 'updatedAt', 'startDate', 'endDate']);
    const fmt = (val: any) => (val == null || val === '' ? '-' : val);
    const fmtDate = (val: any) => {
      if (!val) return '-';
      try { return new Date(val).toLocaleDateString('en-GB'); } catch { return String(val); }
    };

    const headers = ['Sr No', ...fields.map(field => fieldLabels[field] || field)];
    const rows = data.map((row, idx) => [
      idx + 1,
      ...fields.map(field => (dateFields.has(field) ? fmtDate(row[field]) : fmt(row[field]))),
    ]);

    const fileName = `${reportType}-report-${new Date().toISOString().split('T')[0]}.xlsx`;

    await downloadStyledReport({
      fileName,
      title: getReportTitle().toUpperCase(),
      subtitle,
      headers,
      rows,
    });
  };

  // Generate a 4-sheet client-wise Excel report (Stock Summary + Inbound + Outbound + Opening Stock)
  const handleGenerateClientReport = useCallback(async () => {
    if (!filters.clientId) {
      toast.error('Please select a client from the filters first.');
      return;
    }

    try {
      setIsExporting(true);

      const PAGE_SIZE = 100;

      const fetchAllPages = async (path: string): Promise<any[]> => {
        const qp = new URLSearchParams();
        qp.append('clientId', filters.clientId!.toString());
        if (filters.dateFrom) qp.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) qp.append('dateTo', filters.dateTo);
        if (filters.productDescription) qp.append('productDescription', filters.productDescription);
        if (filters.batchNo) qp.append('batchNo', filters.batchNo);
        qp.append('limit', PAGE_SIZE.toString());
        qp.append('page', '1');

        const first = await api.get(`${path}?${qp.toString()}`);
        const items: any[] = first?.data?.items || [];
        const totalPages: number = first?.data?.meta?.totalPages || 1;

        if (totalPages > 1) {
          const rest = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) => {
              const p = new URLSearchParams(qp);
              p.set('page', String(i + 2));
              return api.get(`${path}?${p.toString()}`);
            })
          );
          rest.forEach(r => items.push(...(r?.data?.items || [])));
        }
        return items;
      };

      // Fetch saved field configs using the config-panel client+warehouse selection
      const fetchSheetConfig = async (sheetType: string): Promise<string[]> => {
        try {
          const p = new URLSearchParams();
          const cId = configClientId ?? filters.clientId;
          if (cId) p.append('clientId', cId.toString());
          if (configWarehouseId) p.append('warehouseId', configWarehouseId.toString());
          const res = await api.get(`/report-config/${sheetType}?${p.toString()}`);
          const fields = res?.data?.selectedFields;
          return Array.isArray(fields) && fields.length ? fields : getDefaultSelectedFields(sheetType as ReportType);
        } catch {
          return getDefaultSelectedFields(sheetType as ReportType);
        }
      };

      // Opening Stock = whatever was already in the warehouse before the selected
      // report period started. Queried as [2010-01-01 .. dayBeforeFilterFrom] so it
      // captures everything that existed prior to filters.dateFrom, regardless of dateTo.
      // With no dateFrom set, it falls back to an unfiltered "current warehouse state" snapshot.
      const dayBefore = (isoDate: string): string => {
        const [y, m, d] = isoDate.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        dt.setUTCDate(dt.getUTCDate() - 1);
        return dt.toISOString().split('T')[0];
      };
      const fetchOpeningStockInventory = async (): Promise<any[]> => {
        const qp = new URLSearchParams();
        qp.append('clientId', filters.clientId!.toString());
        qp.append('page', '1');
        qp.append('limit', '10000');
        if (filters.dateFrom) {
          qp.append('dateFrom', '2010-01-01');
          qp.append('dateTo', dayBefore(filters.dateFrom));
        }
        if (filters.productDescription) qp.append('productDescription', filters.productDescription);
        if (filters.batchNo) qp.append('batchNo', filters.batchNo);
        const res = await api.get(`/reports/inventory?${qp.toString()}`);
        return res?.data?.items || [];
      };

      const [inventoryData, inwardData, outwardData, openingStockInventoryData, invFields, inwFields, outwFields, osFields] = await Promise.all([
        fetchAllPages('/reports/inventory'),
        fetchAllPages('/reports/inward'),
        fetchAllPages('/reports/outward'),
        fetchOpeningStockInventory(),
        fetchSheetConfig('mis_stock_summary'),
        fetchSheetConfig('mis_inward'),
        fetchSheetConfig('mis_outward'),
        fetchSheetConfig('mis_opening_stock'),
      ]);

      const clientName =
        inventoryData[0]?.clientName ||
        inwardData[0]?.clientName ||
        outwardData[0]?.clientName ||
        'Client';

      const fmt = (val: any) => (val == null || val === '' ? '-' : val);
      const fmtDate = (val: any) => {
        if (!val) return '-';
        try { return new Date(val).toLocaleDateString('en-GB'); } catch { return String(val); }
      };
      // Grand Total = Master Packing × Pack Size × Total Quantity
      const grandTotal = (masterPacking: any, packSize: any, qty: number) =>
        (parseFloat(String(masterPacking)) || 0) * (parseFloat(String(packSize)) || 0) * (qty || 0);

      // ── Build styled workbook with ExcelJS ──────────────────────────────────
      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = 'WMS';

      // Palette
      const C = {
        orange:  'FFED7D31',
        green:   'FF70AD47',
        dkGreen: 'FF375623',
        ltGreen: 'FFE2EFDA',
        pink:    'FFFCE4D6',
        red:     'FFFF0000',
        yellow:  'FFFFEB9C',
        white:   'FFFFFFFF',
        black:   'FF000000',
        grey:    'FFF2F2F2',
        bdr:     'FFBFBFBF',
        greenBdr:'FF4F7F3F',
      };

      const solid = (argb: string) =>
        ({ type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb } });
      const thin = (argb: string) =>
        ({ style: 'thin' as const, color: { argb } });
      const allBdr = (argb = C.bdr) =>
        ({ top: thin(argb), left: thin(argb), bottom: thin(argb), right: thin(argb) });

      const colLetter = (n: number): string => {
        let r = '';
        while (n > 0) { n--; r = String.fromCharCode(65 + (n % 26)) + r; n = Math.floor(n / 26); }
        return r;
      };

      const styleHeader = (cell: any) => {
        cell.font = { bold: true, size: 10, color: { argb: C.white }, name: 'Calibri' };
        cell.fill = solid(C.green);
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = allBdr(C.greenBdr);
      };

      const styleData = (cell: any, even: boolean, align: 'left'|'center'|'right' = 'left') => {
        cell.fill = solid(even ? C.white : C.grey);
        cell.border = allBdr();
        cell.alignment = { horizontal: align, vertical: 'middle' };
        cell.font = { size: 10, name: 'Calibri' };
      };

      // ── MIS column label & getter maps ───────────────────────────────────────
      const MIS_INW_LABEL: Record<string, string> = {
        mis_gate_entry_no: 'Security Inbound Entry No.',
        mis_client_name: 'Customer Name',
        mis_grn_no: 'GRN No.',
        mis_supplier_name: 'Supplier Name',
        mis_supplier_location: 'Supplier Location',
        mis_date_receipt: 'Date Of Receipt',
        mis_in_time: 'In Time',
        mis_vehicle_no: 'Vehicle No',
        mis_vehicle_type: 'Vehicle Type',
        mis_transporter: 'Transporter Name',
        mis_lr_no: 'LR No',
        mis_inv_no: 'BE No / Inv No',
        mis_inv_date: 'BE / Inv Date',
        mis_item_code: 'Item Code',
        mis_item_desc: 'Item Description',
        mis_pack_size: 'Pack Size',
        mis_uom: 'UOM',
        mis_master_packing: 'Master Packing',
        mis_lot_no: 'Lot No.',
        mis_batch_no: 'BATCH NUMBER',
        mis_mfg_date: 'MF DATE',
        mis_exp_date: 'EX DATE',
        mis_inv_qty: 'Inv Qty',
        mis_rec_qty: 'Rec Qty',
        mis_short_excess: 'Short (-) / Excess (+) Received',
        mis_good: 'GOOD',
        mis_damage: 'Damage / Leakage',
        mis_wt_per_uom: 'Wt per UOM in Kg.',
        mis_total_weight: 'Total Weight in KG',
        mis_grand_total: 'Grand Total',
        mis_handling_type: 'Handling Type',
        mis_remark: 'REMARK',
      };
      const MIS_INW_GETTER: Record<string, (r: any) => any> = {
        mis_gate_entry_no: r => fmt(r.gateEntryNumber),
        mis_client_name: r => fmt(r.clientName),
        mis_grn_no: r => fmt(r.grnNumber),
        mis_supplier_name: r => fmt(r.clientLocationName),
        mis_supplier_location: r => fmt(r.clientLocationName),
        mis_date_receipt: r => { const d = r.approvedAt || r.createdAt; return d ? fmtDate(d) : '-'; },
        mis_in_time: r => { const d = r.approvedAt || r.createdAt; return d ? new Date(d).toTimeString().slice(0, 8) : '-'; },
        mis_vehicle_no: r => fmt(r.grnVehicleNo || r.vehicleNumber),
        mis_vehicle_type: () => 'Truck',
        mis_transporter: r => fmt(r.grnTransporterName),
        mis_lr_no: r => fmt(r.grnLrNumber),
        mis_inv_no: r => fmt(r.invoiceNumber),
        mis_inv_date: r => (r as any).invDate ? fmtDate((r as any).invDate) : '-',
        mis_item_code: r => fmt(r.itemCode),
        mis_item_desc: r => fmt(r.productDescription),
        mis_pack_size: r => fmt(r.packSize),
        mis_uom: r => fmt(r.unitType),
        mis_master_packing: r => fmt(r.masterPacking),
        mis_lot_no: r => fmt(r.lotNo),
        mis_batch_no: r => fmt(r.batchNo),
        mis_mfg_date: r => r.mfgDate ? fmtDate(r.mfgDate) : '-',
        mis_exp_date: r => r.expiryDate ? fmtDate(r.expiryDate) : '-',
        mis_inv_qty: r => fmt(r.expectedQuantity),
        mis_rec_qty: r => (r.goodQty || 0) + (r.damQty || 0) + (r.cdamQty || 0),
        mis_short_excess: r => fmt(r.shortExcess),
        mis_good: r => fmt(r.goodQty),
        mis_damage: r => (r.damQty || 0) + (r.cdamQty || 0),
        mis_wt_per_uom: r => fmt(r.weightPerItem),
        mis_total_weight: r => fmt(r.totalWeight),
        mis_grand_total: r => grandTotal(r.masterPacking, r.packSize, (r.goodQty || 0) + (r.damQty || 0) + (r.cdamQty || 0)),
        mis_handling_type: r => fmt(r.handlingType),
        mis_remark: r => fmt(r.itemRemarks),
      };

      const MIS_OUT_LABEL: Record<string, string> = {
        mis_out_gate_entry_no: 'Security Outbound Entry No.',
        mis_out_client_name: 'Customer Name',
        mis_out_gon_no: 'GON No.',
        mis_out_consignee: 'Consignee Name',
        mis_out_consignee_loc: 'Consignee Location',
        mis_out_date: 'Date Of Dispatch',
        mis_out_time: 'Out Time',
        mis_out_vehicle_no: 'Vehicle No',
        mis_out_vehicle_type: 'Vehicle Type',
        mis_out_transporter: 'Transporter Name',
        mis_out_lr_no: 'LR No',
        mis_out_inv_no: 'Inv No',
        mis_out_inv_date: 'Inv Date',
        mis_out_item_code: 'Item Code',
        mis_out_item_desc: 'Item Description',
        mis_out_pack_size: 'Pack Size',
        mis_out_uom: 'UOM',
        mis_out_master_packing: 'Master Packing',
        mis_out_lot_no: 'Lot No.',
        mis_out_batch_no: 'BATCH NUMBER',
        mis_out_mfg_date: 'MF DATE',
        mis_out_exp_date: 'EX DATE',
        mis_out_inv_qty: 'Inv Qty',
        mis_out_disp_qty: 'Dispatched Qty',
        mis_out_short_excess: 'Short (-) / Excess (+) Dispatched',
        mis_out_wt_per_uom: 'Wt per UOM in Kg.',
        mis_out_total_weight: 'Total Weight in KG',
        mis_out_grand_total: 'Grand Total',
        mis_out_handling_type: 'Handling Type',
        mis_out_remark: 'REMARK',
      };
      const MIS_OUT_GETTER: Record<string, (r: any) => any> = {
        mis_out_gate_entry_no: r => fmt(r.gateEntryNumber),
        mis_out_client_name: r => fmt(r.clientName),
        mis_out_gon_no: r => fmt(r.dispatchNumber),
        mis_out_consignee: r => fmt(r.destination),
        mis_out_consignee_loc: r => fmt(r.destination),
        mis_out_date: r => { const d = r.approvedAt || r.createdAt; return d ? fmtDate(d) : '-'; },
        mis_out_time: r => { const d = r.approvedAt || r.createdAt; return d ? new Date(d).toTimeString().slice(0, 8) : '-'; },
        mis_out_vehicle_no: r => fmt(r.dispatchVehicleNo || r.vehicleNumber),
        mis_out_vehicle_type: () => 'Truck',
        mis_out_transporter: r => fmt(r.dispatchTransporterName),
        mis_out_lr_no: r => fmt(r.lrNumber),
        mis_out_inv_no: r => fmt(r.invoiceNumber),
        mis_out_inv_date: r => (r as any).invDate ? fmtDate((r as any).invDate) : '-',
        mis_out_item_code: r => fmt(r.itemCode),
        mis_out_item_desc: r => fmt(r.productDescription),
        mis_out_pack_size: r => fmt(r.packSize),
        mis_out_uom: r => fmt(r.unitType),
        mis_out_master_packing: r => fmt(r.masterPacking),
        mis_out_lot_no: r => fmt(r.lotNo),
        mis_out_batch_no: r => fmt(r.batchNo),
        mis_out_mfg_date: r => r.mfgDate ? fmtDate(r.mfgDate) : '-',
        mis_out_exp_date: r => r.expiryDate ? fmtDate(r.expiryDate) : '-',
        mis_out_inv_qty: r => fmt(r.expectedQuantity),
        mis_out_disp_qty: r => fmt(r.goodQty),
        mis_out_short_excess: () => '-',
        mis_out_wt_per_uom: r => fmt(r.weightPerItem),
        mis_out_total_weight: r => fmt(r.goodQty && r.weightPerItem ? (r.goodQty * r.weightPerItem).toFixed(2) : r.totalWeight || '-'),
        mis_out_grand_total: r => grandTotal(r.masterPacking, r.packSize, (r.goodQty || 0) + (r.damQty || 0) + (r.cdamQty || 0)),
        mis_out_handling_type: r => fmt(r.handlingType),
        mis_out_remark: r => fmt(r.dispatchRemarks || r.remarks),
      };

      const MIS_SS_LABEL: Record<string, string> = {
        mis_ss_client: 'Customer Name',
        mis_ss_item_code: 'Item Code',
        mis_ss_item_desc: 'Item Description',
        mis_ss_pack_size: 'Pack Size',
        mis_ss_uom: 'UOM',
        mis_ss_master_packing: 'Master Packing',
        mis_ss_lot_no: 'Lot No.',
        mis_ss_batch_no: 'BATCH NUMBER',
        mis_ss_mfg_date: 'MF DATE',
        mis_ss_exp_date: 'EX DATE',
        mis_ss_wt_per_uom: 'Wt per UOM in Kg.',
        mis_ss_opp_good: 'Opp. GOOD',
        mis_ss_opp_dam: 'Opp. Damage / Leakage',
        mis_ss_in_good: 'In GOOD',
        mis_ss_in_dam: 'In Damage / Leakage',
        mis_ss_out_good: 'Out GOOD',
        mis_ss_out_dam: 'Out Damage / Leakage',
        mis_ss_clo_good: 'Clo. GOOD',
        mis_ss_clo_dam: 'Clo. Damage / Leakage',
        mis_ss_clo_kg: 'Clos. In KG',
        mis_ss_grand_total: 'Grand Total',
        mis_ss_remark: 'REMARK',
      };
      // Group each SS key belongs to ('' = no group = base columns)
      const SS_KEY_GROUP: Record<string, string> = {
        mis_ss_client: '', mis_ss_item_code: '', mis_ss_item_desc: '', mis_ss_pack_size: '',
        mis_ss_uom: '', mis_ss_master_packing: '', mis_ss_lot_no: '', mis_ss_batch_no: '',
        mis_ss_mfg_date: '', mis_ss_exp_date: '', mis_ss_wt_per_uom: '',
        mis_ss_opp_good: 'Opening Stock', mis_ss_opp_dam: 'Opening Stock',
        mis_ss_in_good: 'Inbound', mis_ss_in_dam: 'Inbound',
        mis_ss_out_good: 'Outbound', mis_ss_out_dam: 'Outbound',
        mis_ss_clo_good: 'Closing Stock', mis_ss_clo_dam: 'Closing Stock', mis_ss_clo_kg: 'Closing Stock',
        mis_ss_grand_total: '',
        mis_ss_remark: '',
      };
      const SS_GROUP_COLORS: Record<string, string> = {
        '': C.ltGreen,
        'Opening Stock': C.pink,
        'Inbound': C.ltGreen,
        'Outbound': 'FFFCE4D6',
        'Closing Stock': 'FFD6E4BC',
      };

      // ── Sheet 1: Stock Summary (config-driven MIS format) ────────────────────
      // Build per-product-batch movement maps from inward/outward date-filtered data
      type StockKey = string;
      const inwardMap = new Map<StockKey, { inGood: number; inDam: number }>();
      inwardData.forEach(r => {
        const key = `${r.clientName}||${r.itemCode || r.productDescription}||${r.batchNo || ''}`;
        const prev = inwardMap.get(key) || { inGood: 0, inDam: 0 };
        inwardMap.set(key, {
          inGood: prev.inGood + (r.goodQty || 0),
          inDam:  prev.inDam  + (r.damQty || 0) + (r.cdamQty || 0),
        });
      });
      const outwardMap = new Map<StockKey, { outGood: number; outDam: number }>();
      outwardData.forEach(r => {
        const key = `${r.clientName}||${r.itemCode || r.productDescription}||${r.batchNo || ''}`;
        const prev = outwardMap.get(key) || { outGood: 0, outDam: 0 };
        outwardMap.set(key, {
          outGood: prev.outGood + (r.goodQty || 0),
          outDam:  prev.outDam  + (r.damQty || 0) + (r.cdamQty || 0),
        });
      });

      // Build lot-no and remark lookup maps from inward data keyed by product+batch+client
      const inwardLotMap = new Map<string, string>();
      const inwardRemarkMap = new Map<string, string>();
      inwardData.forEach((r: any) => {
        const k = `${r.clientName}||${r.itemCode || r.productDescription}||${r.batchNo || ''}`;
        if (r.lotNo && !inwardLotMap.has(k)) inwardLotMap.set(k, r.lotNo);
        if (r.itemRemarks && !inwardRemarkMap.has(k)) inwardRemarkMap.set(k, r.itemRemarks);
      });

      // Filter to valid SS keys (fall back to all if saved config has no mis_ keys)
      const allSsKeys = Object.keys(MIS_SS_LABEL);
      const ssKeys = invFields.filter(k => allSsKeys.includes(k)).length
        ? invFields.filter(k => allSsKeys.includes(k))
        : allSsKeys;

      const numSsCols = ssKeys.length + 1; // +1 for Sr No
      const lastSsCol = colLetter(numSsCols);
      const ws1 = wb.addWorksheet('Stock Summary');

      for (let c = 1; c <= numSsCols; c++) ws1.getColumn(c).width = c === 1 ? 7 : 18;

      // Row 1 — orange title
      ws1.mergeCells(`A1:${lastSsCol}1`);
      ws1.getRow(1).height = 30;
      const ss_t1 = ws1.getCell('A1');
      ss_t1.value = 'STOCK SUMMARY';
      ss_t1.font = { bold: true, size: 14, color: { argb: C.white }, name: 'Calibri' };
      ss_t1.fill = solid(C.orange);
      ss_t1.alignment = { horizontal: 'center', vertical: 'middle' };

      // Row 2 — dynamic group header spans
      ws1.getRow(2).height = 18;
      ws1.getRow(2).getCell(1).fill = solid(C.ltGreen); // Sr No cell
      let colIdx2 = 2;
      let si = 0;
      while (si < ssKeys.length) {
        const grp = SS_KEY_GROUP[ssKeys[si]] ?? '';
        let sj = si + 1;
        while (sj < ssKeys.length && (SS_KEY_GROUP[ssKeys[sj]] ?? '') === grp) sj++;
        const span = sj - si;
        if (span > 1) ws1.mergeCells(2, colIdx2, 2, colIdx2 + span - 1);
        const gc2 = ws1.getRow(2).getCell(colIdx2);
        gc2.fill = solid(SS_GROUP_COLORS[grp] ?? C.ltGreen);
        if (grp) {
          gc2.value = grp;
          gc2.font = { bold: true, size: 9, color: { argb: C.black }, name: 'Calibri' };
          gc2.alignment = { horizontal: 'center', vertical: 'middle' };
          gc2.border = allBdr();
        }
        colIdx2 += span;
        si = sj;
      }

      // Row 3 — column headers
      ws1.getRow(3).height = 22;
      ['Sr No', ...ssKeys.map(k => MIS_SS_LABEL[k] || k)].forEach((h, ci) => {
        const cell = ws1.getRow(3).getCell(ci + 1);
        cell.value = h;
        styleHeader(cell);
      });

      // Rows 4+ — data with computed opening/closing
      inventoryData.forEach((item, idx) => {
        const key = `${item.clientName}||${item.itemCode || item.productDescription}||${item.batchNo || ''}`;
        const inw = inwardMap.get(key) || { inGood: 0, inDam: 0 };
        const out = outwardMap.get(key) || { outGood: 0, outDam: 0 };
        const cloGood = Number(item.goodQty ?? 0);
        const cloDam  = Number(item.cdamQty ?? 0) + Number(item.damQty ?? 0);
        const oppGood = Math.max(0, cloGood - inw.inGood + out.outGood);
        const oppDam  = Math.max(0, cloDam  - inw.inDam  + out.outDam);
        const cloKg   = item.weightPerItem ? (cloGood * Number(item.weightPerItem)).toFixed(2) : '-';
        const mp = (item as any).masterPacking;
        const ps = (item as any).packSize;

        const computed: Record<string, any> = {
          mis_ss_client: fmt(item.clientName),
          mis_ss_item_code: fmt(item.itemCode),
          mis_ss_item_desc: fmt(item.productDescription),
          mis_ss_pack_size: fmt(ps) || '-',
          mis_ss_uom: fmt(item.unitType),
          mis_ss_master_packing: fmt(mp) || '-',
          mis_ss_lot_no: inwardLotMap.get(key) || '-',
          mis_ss_batch_no: fmt(item.batchNo),
          mis_ss_mfg_date: item.mfgDate ? fmtDate(item.mfgDate) : '-',
          mis_ss_exp_date: item.expiryDate ? fmtDate(item.expiryDate) : '-',
          mis_ss_wt_per_uom: fmt(item.weightPerItem),
          // Opening/Inbound/Outbound/Closing Good & Damage show weight value (Master Packing × Pack Size × Qty), not raw qty
          mis_ss_opp_good: grandTotal(mp, ps, oppGood),
          mis_ss_opp_dam: grandTotal(mp, ps, oppDam),
          mis_ss_in_good: grandTotal(mp, ps, inw.inGood),
          mis_ss_in_dam: grandTotal(mp, ps, inw.inDam),
          mis_ss_out_good: grandTotal(mp, ps, out.outGood),
          mis_ss_out_dam: grandTotal(mp, ps, out.outDam),
          mis_ss_clo_good: grandTotal(mp, ps, cloGood),
          mis_ss_clo_dam: grandTotal(mp, ps, cloDam),
          mis_ss_clo_kg: cloKg,
          mis_ss_grand_total: grandTotal(mp, ps, cloGood + cloDam),
          mis_ss_remark: inwardRemarkMap.get(key) || '-',
        };

        const row = ws1.getRow(4 + idx);
        row.height = 16;
        [idx + 1, ...ssKeys.map(k => computed[k] ?? '-')].forEach((v, ci) => {
          const cell = row.getCell(ci + 1);
          cell.value = v;
          styleData(cell, idx % 2 === 0, ci === 0 ? 'center' : 'left');
        });
      });

      // ── Helper: build Inbound / Dispatch sheets ───────────────────────────────
      const buildDetailSheet = (
        name: string,
        titleText: string,
        headers: string[],
        rowsData: any[][]
      ) => {
        const ws = wb.addWorksheet(name);
        const numCols = headers.length;
        const lastC = colLetter(numCols);

        ws.getColumn(1).width = 7;
        for (let c = 2; c <= numCols; c++) ws.getColumn(c).width = 20;

        ws.mergeCells(`A1:${lastC}1`);
        ws.getRow(1).height = 30;
        const tc = ws.getCell('A1');
        tc.value = titleText;
        tc.font = { bold: true, size: 13, color: { argb: C.white }, name: 'Calibri' };
        tc.fill = solid(C.green);
        tc.alignment = { horizontal: 'center', vertical: 'middle' };

        ws.mergeCells(`A2:${lastC}2`);
        ws.getRow(2).height = 6;
        ws.getCell('A2').fill = solid(C.ltGreen);

        ws.getRow(3).height = 24;
        headers.forEach((h, ci) => {
          const cell = ws.getRow(3).getCell(ci + 1);
          cell.value = h;
          styleHeader(cell);
        });

        rowsData.forEach((rowData, idx) => {
          const row = ws.getRow(4 + idx);
          row.height = 16;
          rowData.forEach((v, ci) => {
            const cell = row.getCell(ci + 1);
            cell.value = v;
            styleData(cell, idx % 2 === 0, ci === 0 ? 'center' : 'left');
          });
        });
      };

      // ── MIS Format: Inbound sheet (config-driven) ─────────────────────────────
      const allInwKeys = Object.keys(MIS_INW_LABEL);
      const inwSelectedKeys = inwFields.filter(k => allInwKeys.includes(k)).length
        ? inwFields.filter(k => allInwKeys.includes(k))
        : allInwKeys;

      buildDetailSheet(
        'Inbound Details',
        'INBOUND DETAILS',
        ['Sr No', ...inwSelectedKeys.map(k => MIS_INW_LABEL[k] || k)],
        inwardData.map((r, idx) => [idx + 1, ...inwSelectedKeys.map(k => MIS_INW_GETTER[k]?.(r) ?? '-')])
      );

      // ── MIS Format: Outbound sheet (config-driven) ─────────────────────────────
      const allOutKeys = Object.keys(MIS_OUT_LABEL);
      const outwSelectedKeys = outwFields.filter(k => allOutKeys.includes(k)).length
        ? outwFields.filter(k => allOutKeys.includes(k))
        : allOutKeys;

      buildDetailSheet(
        'Dispatch Details',
        'DISPATCH / OUTBOUND DETAILS',
        ['Sr No', ...outwSelectedKeys.map(k => MIS_OUT_LABEL[k] || k)],
        outwardData.map((r, idx) => [idx + 1, ...outwSelectedKeys.map(k => MIS_OUT_GETTER[k]?.(r) ?? '-')])
      );

      // ── Opening Stock sheet (config-driven) — one row per product/batch, same opening-balance math as Stock Summary ──
      const MIS_OS_LABEL: Record<string, string> = {
        os_client: 'Customer Name',
        os_date: 'Date',
        os_item_code: 'Item Code',
        os_item_desc: 'Description',
        os_pack_size: 'Pack Size',
        os_uom: 'UOM',
        os_master_packing: 'Master Packing',
        os_lot_no: 'Lot No.',
        os_batch_no: 'Batch Number',
        os_mfg_date: 'Mfg Date',
        os_exp_date: 'Ex Date',
        os_qty: 'Qty',
        os_good: 'Good',
        os_damage: 'Damage / Leakage',
        os_wt_per_uom: 'Net Wt per UOM in Kg.',
        os_total_weight: 'Total Weight in KG',
        os_grand_total: 'Grand Total',
        os_remark: 'Remark',
      };
      const allOsKeys = Object.keys(MIS_OS_LABEL);
      const osSelectedKeys = osFields.filter(k => allOsKeys.includes(k)).length
        ? osFields.filter(k => allOsKeys.includes(k))
        : allOsKeys;

      // Opening Stock shows whatever stock currently exists in the warehouse —
      // sourced from its own clientId-only, unfiltered inventory fetch, not the
      // (possibly date-filtered) inventoryData used by Stock Summary above.
      const openingStockDate = filters.dateFrom ? fmtDate(filters.dateFrom) : '-';
      buildDetailSheet(
        'Opening Stock',
        'OPENING STOCK',
        ['Sr No', ...osSelectedKeys.map(k => MIS_OS_LABEL[k] || k)],
        openingStockInventoryData.map((item, idx) => {
          const key = `${item.clientName}||${item.itemCode || item.productDescription}||${item.batchNo || ''}`;
          const cloGood = Number(item.goodQty ?? 0);
          const cloDam  = Number(item.cdamQty ?? 0) + Number(item.damQty ?? 0);
          const cloTotalWeight = cloGood && item.weightPerItem ? (cloGood * Number(item.weightPerItem)).toFixed(2) : '-';
          const computed: Record<string, any> = {
            os_client: fmt(item.clientName),
            os_date: openingStockDate,
            os_item_code: fmt(item.itemCode),
            os_item_desc: fmt(item.productDescription),
            os_pack_size: fmt((item as any).packSize),
            os_uom: fmt(item.unitType),
            os_master_packing: fmt((item as any).masterPacking),
            os_lot_no: inwardLotMap.get(key) || '-',
            os_batch_no: fmt(item.batchNo),
            os_mfg_date: item.mfgDate ? fmtDate(item.mfgDate) : '-',
            os_exp_date: item.expiryDate ? fmtDate(item.expiryDate) : '-',
            os_qty: cloGood + cloDam,
            os_good: cloGood,
            os_damage: cloDam,
            os_wt_per_uom: fmt(item.weightPerItem),
            os_total_weight: cloTotalWeight,
            os_grand_total: grandTotal((item as any).masterPacking, (item as any).packSize, cloGood + cloDam),
            os_remark: inwardRemarkMap.get(key) || '-',
          };
          return [idx + 1, ...osSelectedKeys.map(k => computed[k] ?? '-')];
        })
      );

      // ── Download ─────────────────────────────────────────────────────────────
      const fileName = `${clientName}_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Client report generated for ${clientName} (${inventoryData.length} inventory, ${inwardData.length} inward, ${outwardData.length} outward, ${openingStockInventoryData.length} opening stock records).`);
    } catch (error: any) {
      console.error('Client report generation failed:', error);
      toast.error(error?.message || 'Failed to generate client report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, configClientId, configWarehouseId]);

  const getReportTitle = useCallback(() => {
    const titles = {
      inventory: 'Inventory Report',
      inward: 'Inbound Report',
      outward: 'Outbound Report',
      damage: 'Damage Report',
      billing: 'Billing Report',
      'client-utilization': 'Client Utilization Report',
      'additional-space': 'Additional Space Report',
    };
    return titles[reportType];
  }, [reportType]);

  const getReportIcon = useCallback(() => {
    const icons = {
      inventory: <Package className="h-5 w-5" />,
      inward: <TrendingUp className="h-5 w-5" />,
      outward: <BarChart3 className="h-5 w-5" />,
      damage: <AlertTriangle className="h-5 w-5" />,
      billing: <Receipt className="h-5 w-5" />,
      'client-utilization': <Users className="h-5 w-5" />,
      'additional-space': <Package className="h-5 w-5" />,
    };
    return icons[reportType];
  }, [reportType]);

  // Memoize placeholder list to prevent re-creation
  const searchPlaceholders = useMemo(() => [
    'Search reports...',
    'Search by GRN number...',
    'Search by dispatch number...',
    'Search by client name...',
    'Search by product...',
  ], []);

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide" style={{ overflowY: 'auto' }}>
        {/* Header */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex items-center justify-between bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                {getReportIcon()}
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{getReportTitle()}</h2>
                <p className="text-gray-600 mt-1">Analytics and insights dashboard</p>
              </div>
            </div>
          </div>
        </div>

        {/* Report Type Selector */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden mb-4">
          <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b-2 border-gray-300">
            <label className="block text-sm font-semibold text-gray-700">Report Type</label>
          </div>
          <div className="px-1 py-3 lg:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { value: 'inventory', label: 'Inventory', icon: Package },
                { value: 'inward', label: 'Inbound', icon: TrendingUp },
                { value: 'outward', label: 'Outbound', icon: BarChart3 },
                { value: 'damage', label: 'Damage', icon: AlertTriangle },
                { value: 'billing', label: 'Billing', icon: Receipt },
                { value: 'client-utilization', label: 'Client Utilization', icon: Users },
                { value: 'additional-space', label: 'Additional Space', icon: Package },
              ].filter(({ value }) => value !== 'billing' || isAdmin).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => handleReportTypeChange(value as ReportType)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    reportType === value
                      ? 'border-brand-500 bg-gradient-to-br from-brand-50 to-brand-100 text-brand-700 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-5 w-5 mx-auto mb-2" />
                  <div className="text-sm font-medium">{label}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {(isLoading || summaryLoading) && <ReportSkeleton />}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading report</h3>
                <p className="text-sm text-red-700 mt-1">{(error as any)?.message || 'Failed to load report data'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {(isLoading || summaryLoading) && <ReportSkeleton />}

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading report</h3>
                <p className="text-sm text-red-700 mt-1">{(error as any)?.message || 'Failed to load report data'}</p>
              </div>
            </div>
          </div>
        )}

        {/* KPIs - Only re-fetches when filters change (NOT search) */}
        {!summaryLoading && summaryData && (
          <ReportKPIs reportType={reportType} data={summaryData} />
        )}

        {/* Charts - Only re-fetches when filters change (NOT search) */}
        {!summaryLoading && summaryData && (
          <ReportCharts reportType={reportType} data={summaryData} />
        )}

        {/* Summary Info - Shows table record count when table data is available */}
        {!isLoading && reportData && (
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4 mb-6">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center">
                <svg className="h-5 w-5 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-brand-800">
                  Showing <span className="font-semibold">{allItems.length}</span> records
                  {totalRecords > allItems.length && (
                    <span> of <span className="font-semibold">{totalRecords}</span> total</span>
                  )}
                </p>
              </div>
              <p className="text-xs text-brand-600">
                Last updated: {formatDateTime(new Date().toISOString())}
              </p>
            </div>
          </div>
        )}

        {/* Search Bar, Filter Button, and Export CSV - ALWAYS VISIBLE */}
        <div className="bg-white rounded-xl p-4 shadow-lg border-2 border-gray-300 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-3 lg:gap-4 mb-4">
            <SearchBar
              key="reports-search-bar"
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              placeholderList={searchPlaceholders}
              className="flex-1 min-w-[200px]"
              fullWidth={false}
            />

            <div className="flex flex-wrap items-center gap-2 lg:gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-5 py-2.5 bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white rounded-lg transition-all font-medium shadow-md hover:shadow-lg flex items-center flex-shrink-0"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                </svg>
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>

              <div className="flex flex-wrap items-center gap-2">
                {/* Excel Import - Inbound and Outbound reports */}
                {(reportType === 'inward' || reportType === 'outward') && (
                  <>
                    <input
                      ref={importFileInputRef}
                      type="file"
                      accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                      onChange={handleImportFileChange}
                      className="hidden"
                    />
                    <div
                      title={
                        hasPermission(reportType === 'outward' ? 'dispatch:import' : 'grn:import')
                          ? `Import ${reportType} data from Excel`
                          : `You need ${reportType === 'outward' ? 'dispatch:import' : 'grn:import'} permission to import`
                      }
                    >
                      <Button
                        variant="primary"
                        onClick={handleImportClick}
                        disabled={isImporting || !hasPermission(reportType === 'outward' ? 'dispatch:import' : 'grn:import')}
                      >
                        {isImporting ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-2" />
                            Import
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}

                {/* Styled Excel export — available on every report tab */}
                {hasPermission('reports:export') && (
                  <div
                    title={
                      selectedFields.length === 0
                        ? 'Please select at least one field to export'
                        : 'Export as styled Excel file'
                    }
                  >
                    <Button 
                      variant="primary" 
                      onClick={handleExportExcel}
                      disabled={isExporting || !hasPermission('reports:export') || selectedFields.length === 0}
                    >
                      {isExporting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export Excel ({selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''})
                        </>
                      )}
                    </Button>
                  </div>
                )}
                
                {/* Schedule button — configure auto-email per client */}
                {reportType === 'inventory' && hasPermission('reports:export') && (
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-md hover:shadow-lg transition-all"
                    title="Configure scheduled email report per client"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Schedule
                  </button>
                )}

                {/* Client-wise 4-sheet report — only for inventory */}
                {reportType === 'inventory' && hasPermission('reports:export') && (
                  <div title={!filters.clientId ? 'Select a client from filters to generate report' : 'Generate 4-sheet Excel: Stock Summary + Inbound + Dispatch + Opening Stock'}>
                    <button
                      onClick={handleGenerateClientReport}
                      disabled={isExporting || !filters.clientId}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all shadow-md
                        ${(!filters.clientId || isExporting)
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white hover:shadow-lg'
                        }`}
                    >
                      {isExporting ? (
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      {isExporting ? 'Generating...' : 'Client Report (4 Sheets)'}
                    </button>
                    {!filters.clientId && (
                      <p className="text-xs text-amber-600 mt-1 text-center">Select a client first</p>
                    )}
                  </div>
                )}


                {/* Show CSV export for damage, client-utilization, and additional-space reports */}
                {(reportType === 'damage' || reportType === 'client-utilization' || reportType === 'additional-space') && (
                  <div
                    title={
                      hasPermission('reports:export')
                        ? 'Export as Excel file'
                        : 'You need reports:export permission to export'
                    }
                  >
                    <div title={selectedFields.length === 0 ? 'Please select at least one field to export' : 'Export CSV with selected fields'}>
                      <Button 
                        variant="primary" 
                        onClick={handleExportExcel}
                        disabled={isExporting || !hasPermission('reports:export') || selectedFields.length === 0}
                      >
                        {isExporting ? (
                        <>
                          <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Export Excel ({selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''})
                        </>
                      )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Report Field Configuration — toggles the field/grid config panel rendered below, independent of Show/Hide Filters */}
                <button
                  onClick={() => setShowFieldSelector(!showFieldSelector)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm bg-white border-2 border-brand-300 text-brand-700 hover:bg-brand-50 shadow-md hover:shadow-lg transition-all"
                  title="Choose which columns appear in the grid/export for this report"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                  {showFieldSelector ? 'Hide Field Configuration' : 'Report Field Configuration'}
                </button>
              </div>
            </div>
          </div>

          {/* Collapsible Filters */}
          {showFilters && (
            <div className="border-2 border-brand-200 rounded-xl p-6 bg-gradient-to-br from-brand-50 to-white mb-4">
              <ReportFiltersComponent
                reportType={reportType}
                filters={filters}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}

          {/* Report Field Configuration — shown only via its own toolbar button, independent of Filters */}
          {showFieldSelector && (
            <div className="border-2 border-brand-200 rounded-xl p-6 bg-gradient-to-br from-brand-50 to-white space-y-6">
              {reportType === 'inventory' && <div>
                {/* Header row */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-900">Report Field Configuration</h3>
                    <span className="bg-brand-100 text-brand-800 px-2 py-0.5 rounded-full text-xs font-medium">
                      {configPanelFields.length} fields
                    </span>
                    {isSaving && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowFieldSelector(!showFieldSelector)}
                    className="text-xs text-brand-600 hover:text-brand-800 underline"
                  >
                    {showFieldSelector ? 'Hide' : 'Configure Fields'}
                  </button>
                </div>

                {/* Client + Warehouse + Sub-sheet config dropdowns */}
                <div className="bg-gradient-to-br from-brand-50 to-brand-50 border border-brand-200 rounded-xl p-4 mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span className="text-xs font-semibold text-brand-700 uppercase tracking-wide">
                      {configSheetType === 'inventory_grid'
                        ? 'Grid Configuration — columns shown in the inventory table'
                        : 'Report Configuration — columns included in the Excel export'}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {/* Client dropdown */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
                      <ResponsiveSelect
                        options={(clientsList || []).map(c => ({ value: c.id, label: c.companyName }))}
                        value={configClientId ?? ''}
                        onChange={(v) => {
                          const val = v ? parseInt(v) : null;
                          setConfigClientId(val);
                          if (!val) setConfigPanelFields(getDefaultSelectedFields(configSheetType));
                        }}
                        placeholder="— Select Client —"
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-300 focus:border-brand-400 outline-none transition-all"
                      />
                    </div>

                    {/* Warehouse dropdown */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Warehouse</label>
                      <ResponsiveSelect
                        options={(warehousesList || []).map(w => ({ value: w.id, label: w.name }))}
                        value={configWarehouseId ?? ''}
                        onChange={(v) => {
                          const val = v ? parseInt(v) : null;
                          setConfigWarehouseId(val);
                          if (!val) setConfigPanelFields(getDefaultSelectedFields(configSheetType));
                        }}
                        placeholder="— Select Warehouse —"
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-300 focus:border-brand-400 outline-none transition-all"
                      />
                    </div>

                    {/* Sub-sheet / Grid dropdown */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Configuration Type</label>
                      <ResponsiveSelect
                        options={[
                          { value: 'inventory_grid', label: 'Inventory Grid View' },
                          { value: 'mis_stock_summary', label: 'MIS — Stock Summary' },
                          { value: 'mis_inward', label: 'MIS — Inbound Details' },
                          { value: 'mis_outward', label: 'MIS — Dispatch Details' },
                          { value: 'mis_opening_stock', label: 'MIS — Opening Stock' },
                        ]}
                        value={configSheetType}
                        onChange={(v) => {
                          const val = v as 'inventory' | 'inward' | 'outward' | 'inventory_grid' | 'mis_inward' | 'mis_outward' | 'mis_stock_summary' | 'mis_opening_stock';
                          setConfigSheetType(val);
                          setConfigPanelFields(getDefaultSelectedFields(val));
                        }}
                        includeEmpty={false}
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-300 focus:border-brand-400 outline-none transition-all"
                      />
                    </div>
                  </div>

                  {/* Status bar */}
                  <div className="mt-3 flex items-center gap-2">
                    {(() => {
                      const configTypeLabel: Record<string, string> = {
                        inventory: 'Stock Summary (Report)',
                        inventory_grid: 'Inventory Grid View',
                        inward: 'Inbound Details (Report)',
                        outward: 'Dispatch Details (Report)',
                        mis_inward: 'MIS Inbound Details',
                        mis_outward: 'MIS Dispatch Details',
                        mis_stock_summary: 'MIS Stock Summary',
                        mis_opening_stock: 'MIS Opening Stock',
                      };
                      const label = configTypeLabel[configSheetType] ?? configSheetType;
                      return configClientId && configWarehouseId ? (
                        configLoading ? (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading saved config...
                          </span>
                        ) : savedConfig ? (
                          <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                            Config loaded for <span className="font-semibold mx-1">{label}</span> — changes auto-save
                          </span>
                        ) : (
                          <span className="text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
                            No config saved yet for <span className="font-semibold mx-1">{label}</span> — configure fields below
                          </span>
                        )
                      ) : (
                        <span className="text-xs text-gray-500">
                          Select client &amp; warehouse, then choose configuration type
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {showFieldSelector && (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                    {/* Sub-sheet indicator */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          configSheetType === 'mis_stock_summary'  ? 'bg-brand-100 text-brand-700' :
                          configSheetType === 'mis_inward'         ? 'bg-green-100 text-green-700' :
                          configSheetType === 'mis_opening_stock'  ? 'bg-purple-100 text-purple-700' :
                          configSheetType === 'inventory_grid'     ? 'bg-accent-100 text-accent-700' :
                                                                     'bg-orange-100 text-orange-700'
                        }`}>
                          {configSheetType === 'mis_stock_summary'  ? 'Stock Summary' :
                           configSheetType === 'mis_inward'         ? 'Inbound Details' :
                           configSheetType === 'mis_opening_stock'  ? 'Opening Stock' :
                           configSheetType === 'inventory_grid'     ? 'Inventory Grid' :
                                                                      'Dispatch Details'}
                        </span>
                        <span className="text-xs text-gray-500">{configPanelFields.length} fields selected</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleConfigSelectAll}
                          className="px-2 py-1 text-xs bg-brand-600 hover:bg-brand-700 text-white rounded transition-colors"
                        >
                          All
                        </button>
                        <button
                          onClick={handleConfigDeselectAll}
                          className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    {/* Compact Field Grid */}
                    <div className="space-y-2">
                      {Object.entries(configPanelFieldsByCategory).map(([category, fields]) => (
                        <div key={category}>
                          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                            {category}
                          </h4>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                            {fields.map((field) => (
                              <label key={field.key} className="flex items-center space-x-1 cursor-pointer hover:bg-white p-1 rounded text-xs">
                                <input
                                  type="checkbox"
                                  checked={configPanelFields.includes(field.key)}
                                  onChange={() => handleConfigFieldToggle(field.key)}
                                  className="h-3 w-3 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                                />
                                <span className="text-xs text-gray-700 truncate" title={field.label}>{field.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>}

              {/* Report Field Configuration — non-inventory reports */}
              {reportType !== 'inventory' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                      </svg>
                      <h3 className="text-sm font-semibold text-gray-900">Report Field Configuration</h3>
                      <span className="bg-brand-100 text-brand-800 px-2 py-0.5 rounded-full text-xs font-medium">
                        {selectedFields.length} fields
                      </span>
                      {isNonInvGridSaving && (
                        <span className="flex items-center gap-1 text-xs text-green-600">
                          <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Saving...
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setShowFieldSelector(!showFieldSelector)}
                      className="text-xs text-brand-600 hover:text-brand-800 underline"
                    >
                      {showFieldSelector ? 'Hide' : 'Configure Fields'}
                    </button>
                  </div>

                  <div className="text-xs text-gray-500 mb-2">
                    Columns saved globally for this report type — auto-saved on change.
                  </div>

                  {showFieldSelector && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <button
                          onClick={handleSelectAllFields}
                          className="px-2 py-1 text-xs bg-brand-600 hover:bg-brand-700 text-white rounded transition-colors"
                        >
                          All
                        </button>
                        <button
                          onClick={handleDeselectAllFields}
                          className="px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                      <div className="space-y-2">
                        {Object.entries(fieldsByCategory).map(([category, fields]) => (
                          <div key={category}>
                            <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                              {category}
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                              {fields.map(field => (
                                <label
                                  key={field.key}
                                  className="flex items-center gap-1.5 p-1.5 rounded hover:bg-white cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedFields.includes(field.key)}
                                    onChange={() => handleFieldToggle(field.key)}
                                    className="w-3.5 h-3.5 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                                  />
                                  <span className="text-xs text-gray-700 truncate" title={field.label}>{field.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Table - Shows when report data is available */}
        {!isLoading && reportData && (
          <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
            <div className="px-1 py-3 lg:p-5">
              <ReportTable
                reportType={reportType}
                data={allItems}
                onRowClick={handleRowClick}
                onLoadMore={handleLoadMore}
                hasMore={hasNextPage}
                isLoadingMore={isFetchingNextPage}
                selectedFields={selectedFields}
                availableFields={availableFields}
                onFieldToggle={handleFieldToggle}
              />
            </div>
          </div>
        )}

        {/* Show message when no data available */}
        {!isLoading && !reportData && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
            <div className="text-gray-500">
              <svg className="h-12 w-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium">No report data available</p>
              <p className="text-sm mt-1">Try adjusting your filters or check back later</p>
            </div>
          </div>
        )}

        {/* Detail Drawer */}
        {showDetailDrawer && selectedRow && (
          <ReportDetailDrawer
            reportType={reportType}
            row={selectedRow}
            onClose={() => {
              setShowDetailDrawer(false);
              setSelectedRow(null);
            }}
          />
        )}
      </div>

      {/* ── Schedule Report Modal ───────────────────────────────────────────── */}
      {showScheduleModal && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-3 lg:p-0 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl lg:rounded-2xl shadow-2xl w-full max-w-lg mx-0 lg:mx-4 max-h-[85vh] overflow-y-auto animate-slide-up lg:animate-none">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h2 className="text-white font-semibold text-lg">Schedule Daily Report</h2>
              </div>
              <button
                onClick={() => { setShowScheduleModal(false); setEmailError(''); }}
                className="text-white/70 hover:text-white transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-500">
                Configure a daily schedule to auto-generate the 4-sheet client report and email it to a recipient. Fields are taken from the saved report configuration per client &amp; warehouse.
              </p>

              {/* Client */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Client <span className="text-red-500">*</span></label>
                <ResponsiveSelect
                  options={(clientsList || []).map(c => ({ value: c.id, label: c.companyName }))}
                  value={scheduleForm.clientId ?? ''}
                  onChange={(v) => {
                    const val = v ? parseInt(v) : null;
                    setScheduleForm(prev => ({ ...prev, clientId: val }));
                  }}
                  placeholder="— Select Client —"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                />
              </div>

              {/* Warehouse */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Warehouse <span className="text-gray-400">(optional)</span></label>
                <ResponsiveSelect
                  options={(warehousesList || []).map(w => ({ value: w.id, label: w.name }))}
                  value={scheduleForm.warehouseId ?? ''}
                  onChange={(v) => {
                    const val = v ? parseInt(v) : null;
                    setScheduleForm(prev => ({ ...prev, warehouseId: val }));
                  }}
                  placeholder="— All Warehouses —"
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Recipient Email(s) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={scheduleForm.recipientEmail}
                  onChange={e => {
                    const val = e.target.value;
                    setScheduleForm(prev => ({ ...prev, recipientEmail: val }));
                    setEmailError(validateEmails(val));
                  }}
                  onBlur={e => setEmailError(validateEmails(e.target.value))}
                  placeholder="email@example.com, another@example.com"
                  className={`w-full text-sm border rounded-lg px-3 py-2 focus:ring-2 outline-none transition-colors ${
                    emailError
                      ? 'border-red-400 focus:ring-red-200 focus:border-red-500 bg-red-50'
                      : 'border-gray-300 focus:ring-purple-300 focus:border-purple-400'
                  }`}
                />
                {emailError ? (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {emailError}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 mt-1">Separate multiple emails with commas</p>
                )}
              </div>

              {/* Time + Timezone row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Schedule Time</label>
                  <input
                    type="time"
                    value={scheduleForm.scheduleTime}
                    onChange={e => setScheduleForm(prev => ({ ...prev, scheduleTime: e.target.value }))}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Timezone</label>
                  <ResponsiveSelect
                    options={[
                      { value: 'Asia/Kolkata', label: 'IST (India)' },
                      { value: 'UTC', label: 'UTC' },
                      { value: 'Asia/Dubai', label: 'GST (Dubai)' },
                      { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
                      { value: 'Europe/London', label: 'GMT (London)' },
                      { value: 'America/New_York', label: 'EST (New York)' },
                    ]}
                    value={scheduleForm.timezone}
                    onChange={(v) => setScheduleForm(prev => ({ ...prev, timezone: v }))}
                    includeEmpty={false}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none"
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setScheduleForm(prev => ({ ...prev, isActive: !prev.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scheduleForm.isActive ? 'bg-purple-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${scheduleForm.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
                <span className="text-sm text-gray-700">
                  {scheduleForm.isActive ? 'Enabled — report will send automatically' : 'Disabled — no emails will be sent'}
                </span>
              </div>

              {/* Existing schedule status */}
              {scheduleData && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-purple-700">Existing schedule found</p>
                    <p className="text-xs text-purple-600">
                      Last sent: {scheduleData.lastRunDate ?? 'Never'}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteSchedule()}
                    disabled={isDeletingSchedule}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                  >
                    {isDeletingSchedule ? 'Removing...' : 'Remove schedule'}
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowScheduleModal(false); setEmailError(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => saveSchedule()}
                disabled={isSavingSchedule || !scheduleForm.clientId || !scheduleForm.recipientEmail.trim() || !!emailError}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition-all
                  ${(isSavingSchedule || !scheduleForm.clientId || !scheduleForm.recipientEmail.trim() || !!emailError)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white shadow-md hover:shadow-lg'
                  }`}
              >
                {isSavingSchedule ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Report Table Component
interface ReportTableProps {
  reportType: ReportType;
  data: any[];
  onRowClick: (row: any) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  selectedFields: string[];
  availableFields: { key: string; label: string; category: string }[];
  onFieldToggle: (key: string) => void;
}

function ReportTable({ reportType, data, onRowClick, onLoadMore, hasMore, isLoadingMore, selectedFields, availableFields, onFieldToggle }: ReportTableProps) {
  const [showAddField, setShowAddField] = useState(false);

  // Add click handler wrapper
  const handleRowClick = (row: any) => {
    onRowClick(row);
  };

  // Generic column builders — used for the long tail of fields added to
  // Report Config (getAvailableFields) so every selectable field also has a
  // matching on-screen grid column, not just an export-only entry.
  const textColumn = (key: string, label: string, width = '160px') => ({
    key,
    label,
    width,
    render: (value: string) => (
      <div className="text-sm truncate" title={value || '-'}>{value || '-'}</div>
    ),
  });
  const numberColumn = (key: string, label: string, width = '100px') => ({
    key,
    label,
    width,
    render: (value: number) => (
      <div className="font-semibold text-gray-900">{value ?? 0}</div>
    ),
  });
  const dateColumn = (key: string, label: string, width = '140px') => ({
    key,
    label,
    width,
    render: (value: string) => (
      <div className="text-sm text-gray-500">{formatDateOnly(value)}</div>
    ),
  });
  const dateTimeColumn = (key: string, label: string, width = '160px') => ({
    key,
    label,
    width,
    render: (value: string) => (
      <div className="text-sm text-gray-500">{formatDateTime(value)}</div>
    ),
  });
  // Grand Total = Master Packing × Pack Size × qty(row) — qty computed per report type
  const grandTotalColumn = (qty: (row: any) => number, width = '110px') => ({
    key: 'grandTotal',
    label: 'Grand Total',
    width,
    render: (_value: number, row: any) => {
      const total = (parseFloat(String(row.masterPacking)) || 0) * (parseFloat(String(row.packSize)) || 0) * qty(row);
      return <div className="font-bold text-gray-900">{total}</div>;
    },
  });

  const getColumns = () => {
    switch (reportType) {
      case 'inventory':
        return [
          { 
            key: 'clientName', 
            label: 'Client', 
            width: '160px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'productCategoryName', 
            label: 'Category', 
            width: '130px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          { 
            key: 'itemCode', 
            label: 'Item Code', 
            width: '110px',
            render: (value: string) => (
              <div className="text-sm font-mono truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          { 
            key: 'skuCode', 
            label: 'SKU', 
            width: '110px',
            render: (value: string) => (
              <div className="text-sm font-mono truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          { 
            key: 'productDescription', 
            label: 'Description', 
            width: '220px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          {
            key: 'batchNo',
            label: 'Batch',
            width: '100px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          textColumn('customerSapCode', 'SAP Code', '120px'),
          textColumn('unitType', 'Unit Type', '100px'),
          dateColumn('mfgDate', 'MFG Date'),
          dateColumn('expiryDate', 'Exp Date'),
          numberColumn('weightPerItem', 'Weight/Item (kg)', '130px'),
          {
            key: 'goodQty',
            label: 'Good',
            width: '80px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'cdamQty',
            label: 'CDAM',
            width: '80px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'damQty',
            label: 'DAM',
            width: '80px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'totalQty',
            label: 'Total',
            width: '90px',
            render: (value: number, row: any) => (
              <div className="font-bold text-gray-900">
                {value || ((row.goodQty || 0) + (row.cdamQty || 0) + (row.damQty || 0))}
              </div>
            ),
          },
          grandTotalColumn(row => row.totalQty || ((row.goodQty || 0) + (row.cdamQty || 0) + (row.damQty || 0))),
          {
            key: 'palletCount',
            label: 'Pallets',
            width: '80px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'storageLocations',
            label: 'Storage',
            width: '180px',
            render: (value: string) => (
              <div className="text-xs truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          textColumn('packSize', 'Pack Size', '100px'),
          textColumn('masterPacking', 'Master Packing', '120px'),
          numberColumn('totalWeight', 'Total Weight (kg)', '140px'),
          dateTimeColumn('updatedAt', 'Last Updated'),
        ];

      case 'inward':
        return [
          { 
            key: 'grnNumber', 
            label: 'GRN Number', 
            width: '140px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'clientName', 
            label: 'Client', 
            width: '200px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'productDescription', 
            label: 'Product', 
            width: '250px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'batchNo', 
            label: 'Batch', 
            width: '120px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          {
            key: 'goodQty',
            label: 'Good',
            width: '100px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">+{value || 0}</div>
            ),
          },
          {
            key: 'palletCount',
            label: 'Pallets',
            width: '100px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'storageLocations',
            label: 'Storage Locations',
            width: '250px',
            render: (value: string) => (
              <div className="truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          {
            key: 'grnStatus',
            label: 'Status',
            width: '120px',
            render: (value: string) => (
              <Badge variant="secondary" rounded>
                {value?.toUpperCase()}
              </Badge>
            ),
          },
          {
            key: 'createdAt',
            label: 'Date',
            width: '160px',
            render: (value: string) => (
              <div className="text-sm text-gray-500">{formatDateTime(value)}</div>
            ),
          },
          {
            key: 'itemRemarks',
            label: 'Remarks',
            width: '200px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          {
            key: 'clientLocationName',
            label: 'Client Location',
            width: '180px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          textColumn('gateEntryNumber', 'Gate Entry Number', '150px'),
          textColumn('itemCode', 'Item Code', '120px'),
          textColumn('productCategoryName', 'Category', '140px'),
          textColumn('unitType', 'Unit Type', '100px'),
          numberColumn('weightPerItem', 'Weight/Item (kg)', '130px'),
          textColumn('lotNo', 'Lot No', '120px'),
          textColumn('handlingType', 'Handling Type', '120px'),
          textColumn('packSize', 'Pack Size', '100px'),
          textColumn('masterPacking', 'Master Packing', '120px'),
          dateColumn('mfgDate', 'MFG Date'),
          dateColumn('expiryDate', 'Expiry Date'),
          numberColumn('damQty', 'DAM Quantity', '100px'),
          numberColumn('cdamQty', 'CDAM Quantity', '100px'),
          numberColumn('shortExcess', 'Short/Excess', '110px'),
          numberColumn('totalWeight', 'Total Weight (kg)', '140px'),
          grandTotalColumn(row => (row.goodQty || 0) + (row.damQty || 0) + (row.cdamQty || 0)),
          textColumn('createdBy', 'Created By', '140px'),
          textColumn('approvedBy', 'Approved By', '140px'),
          dateTimeColumn('approvedAt', 'Approved At'),
          textColumn('remarks', 'Gate Entry Remarks', '200px'),
          textColumn('vehicleNumber', 'Vehicle Number', '130px'),
          textColumn('driverName', 'Driver Name', '140px'),
          textColumn('driverPhone', 'Driver Phone', '130px'),
          textColumn('grnVehicleNo', 'GRN Vehicle No', '130px'),
          textColumn('grnTransporterName', 'GRN Transporter Name', '180px'),
          textColumn('grnLrNumber', 'GRN LR Number', '130px'),
          dateColumn('grnLrDate', 'GRN LR Date'),
          textColumn('invoiceNumber', 'Invoice Number', '140px'),
          dateColumn('invDate', 'Invoice Date'),
          textColumn('ewayBillNumber', 'E-Way Bill Number', '160px'),
          textColumn('packingSlipNumber', 'Packing Slip Number', '160px'),
          numberColumn('expectedQuantity', 'Expected Quantity', '140px'),
          textColumn('dockNumber', 'Dock Number', '120px'),
        ];

      case 'outward':
        return [
          { 
            key: 'dispatchNumber', 
            label: 'Dispatch Number', 
            width: '140px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'clientName', 
            label: 'Client', 
            width: '200px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'productDescription', 
            label: 'Product', 
            width: '250px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'batchNo', 
            label: 'Batch', 
            width: '120px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          {
            key: 'goodQty',
            label: 'Good',
            width: '90px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">-{value || 0}</div>
            ),
          },
          {
            key: 'cdamQty',
            label: 'CDAM',
            width: '90px',
            render: (value: number) => (
              <div className="font-semibold text-amber-700">-{value || 0}</div>
            ),
          },
          {
            key: 'damQty',
            label: 'DAM',
            width: '90px',
            render: (value: number) => (
              <div className="font-semibold text-red-700">-{value || 0}</div>
            ),
          },
          {
            key: 'palletCount',
            label: 'Pallets',
            width: '100px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'shortExcess',
            label: 'Short (-) / Excess (+)',
            width: '150px',
            // Negative = short of the gate entry's expected quantity, positive
            // = excess, null = no expected quantity was recorded to compare
            // against (not the same as 0 — a genuine match).
            render: (value: number | null) =>
              value == null ? (
                <div className="text-gray-400 text-sm">—</div>
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                  value < 0 ? 'bg-red-100 text-red-700' : value > 0 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                }`}>
                  {value > 0 ? `+${value}` : value}
                </span>
              ),
          },
          {
            key: 'storageLocations',
            label: 'Storage Locations',
            width: '250px',
            render: (value: string) => (
              <div className="truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          {
            key: 'dispatchStatus',
            label: 'Status',
            width: '120px',
            render: (value: string) => (
              <Badge variant="secondary" rounded>
                {value?.toUpperCase()}
              </Badge>
            ),
          },
          {
            key: 'createdAt',
            label: 'Date',
            width: '160px',
            render: (value: string) => (
              <div className="text-sm text-gray-500">{formatDateTime(value)}</div>
            ),
          },
          {
            key: 'remarks',
            label: 'Gate Entry Remarks',
            width: '200px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          {
            key: 'dispatchRemarks',
            label: 'Dispatch Remarks',
            width: '200px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          {
            key: 'destination',
            label: 'Destination',
            width: '180px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
          textColumn('gateEntryNumber', 'Gate Entry Number', '150px'),
          textColumn('itemCode', 'Item Code', '120px'),
          textColumn('productCategoryName', 'Category', '140px'),
          textColumn('unitType', 'Unit Type', '100px'),
          numberColumn('weightPerItem', 'Weight/Item (kg)', '130px'),
          textColumn('lotNo', 'Lot No', '120px'),
          textColumn('handlingType', 'Handling Type', '120px'),
          textColumn('packSize', 'Pack Size', '100px'),
          textColumn('masterPacking', 'Master Packing', '120px'),
          dateColumn('mfgDate', 'MFG Date'),
          dateColumn('expiryDate', 'Expiry Date'),
          grandTotalColumn(row => (row.goodQty || 0) + (row.damQty || 0) + (row.cdamQty || 0)),
          textColumn('createdBy', 'Created By', '140px'),
          textColumn('approvedBy', 'Approved By', '140px'),
          dateTimeColumn('approvedAt', 'Approved At'),
          textColumn('vehicleNumber', 'Vehicle Number', '130px'),
          textColumn('driverName', 'Driver Name', '140px'),
          textColumn('driverPhone', 'Driver Phone', '130px'),
          textColumn('dispatchVehicleNo', 'Dispatch Vehicle No', '150px'),
          textColumn('dispatchTransporterName', 'Dispatch Transporter Name', '190px'),
          textColumn('lrNumber', 'LR Number', '130px'),
          dateColumn('dispatchLrDate', 'Dispatch LR Date'),
          textColumn('invoiceNumber', 'Invoice Number', '140px'),
          dateColumn('invDate', 'Invoice Date'),
          textColumn('ewayBillNumber', 'E-Way Bill Number', '160px'),
          textColumn('eWaybillNumber', 'E-Waybill Number (Dispatch)', '190px'),
          textColumn('packingSlipNumber', 'Packing Slip Number', '160px'),
          numberColumn('expectedQuantity', 'Expected Quantity', '140px'),
          textColumn('dockNumber', 'Dock Number', '120px'),
        ];

      case 'damage':
        return [
          { 
            key: 'clientName', 
            label: 'Client', 
            width: '200px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'productDescription', 
            label: 'Product', 
            width: '250px',
            render: (value: string) => (
              <div className="truncate" title={value}>
                {value}
              </div>
            )
          },
          { 
            key: 'batchNo', 
            label: 'Batch', 
            width: '120px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value || '-'}>
                {value || '-'}
              </div>
            )
          },
          {
            key: 'damQty',
            label: 'DAM',
            width: '100px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'cdamQty',
            label: 'CDAM',
            width: '100px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{value || 0}</div>
            ),
          },
          {
            key: 'totalDamaged',
            label: 'Total',
            width: '100px',
            render: (value: number) => (
              <div className="font-bold text-gray-900">{value || 0}</div>
            ),
          },
          numberColumn('grandTotal', 'Grand Total', '110px'),
          {
            key: 'lbdZoneCodes',
            label: 'LBD Zone / Pallet',
            width: '220px',
            render: (value: string, row: any) => {
              if (!row.lbdStorageMode) {
                return <div className="text-sm text-gray-400">-</div>;
              }
              return (
                <div className="text-sm flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-orange-100 text-orange-800 border border-orange-300 whitespace-nowrap">
                    {row.lbdStorageMode === 'shared' ? 'SHARED' : 'DEDICATED'}
                  </span>
                  <span className="text-gray-900">{value || '-'}</span>
                  {row.lbdStorageMode === 'shared' && row.lbdSharedPalletRef && (
                    <span className="text-gray-500">({row.lbdSharedPalletRef})</span>
                  )}
                </div>
              );
            },
          },
          {
            key: 'updatedAt',
            label: 'Updated',
            width: '160px',
            render: (value: string) => (
              <div className="text-sm text-gray-500">{formatDateTime(value)}</div>
            ),
          },
          textColumn('grnNumber', 'GRN Number', '140px'),
          textColumn('grnStatus', 'GRN Status', '110px'),
          textColumn('lbdStorageMode', 'LBD Storage Mode', '140px'),
          numberColumn('lbdPalletCount', 'LBD Pallet Count', '140px'),
          textColumn('lbdSharedPalletRef', 'Shared Pallet Ref', '150px'),
          textColumn('lbdRemark', 'LBD Remark', '180px'),
          dateTimeColumn('createdAt', 'Date Created'),
        ];

      case 'billing':
        return [
          {
            key: 'invoiceNumber',
            label: 'Invoice #',
            width: '150px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>{value}</div>
            ),
          },
          {
            key: 'clientName',
            label: 'Client',
            width: '180px',
            render: (value: string) => (
              <div className="truncate" title={value}>{value}</div>
            ),
          },
          {
            key: 'chargeType',
            label: 'Charge Type',
            width: '150px',
            render: (value: string) => (
              <div className="text-sm capitalize">{(value || '').replace(/_/g, ' ')}</div>
            ),
          },
          {
            key: 'description',
            label: 'Description',
            width: '280px',
            render: (value: string) => (
              <div className="text-sm truncate" title={value}>{value || '-'}</div>
            ),
          },
          {
            key: 'quantity',
            label: 'Qty',
            width: '90px',
            render: (value: number, row: any) => (
              <div className="text-sm text-gray-900">{value ?? 0} {row.unit || ''}</div>
            ),
          },
          {
            key: 'rate',
            label: 'Rate (₹)',
            width: '100px',
            render: (value: number) => (
              <div className="text-sm text-gray-900">{(value ?? 0).toLocaleString('en-IN')}</div>
            ),
          },
          {
            key: 'amount',
            label: 'Amount (₹)',
            width: '120px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">{(value ?? 0).toLocaleString('en-IN')}</div>
            ),
          },
          {
            key: 'invoiceStatus',
            label: 'Invoice Status',
            width: '120px',
            render: (value: string) => (
              <div className="text-sm capitalize">{value || '-'}</div>
            ),
          },
          {
            key: 'invoiceCreatedAt',
            label: 'Invoice Date',
            width: '160px',
            render: (value: string) => (
              <div className="text-sm text-gray-500">{value ? formatDateTime(value) : '-'}</div>
            ),
          },
          textColumn('warehouseName', 'Warehouse', '150px'),
          dateColumn('billingPeriodStart', 'Billing Period Start'),
          dateColumn('billingPeriodEnd', 'Billing Period End'),
          textColumn('unit', 'Unit', '90px'),
          textColumn('referenceType', 'Reference Type', '130px'),
          textColumn('referenceId', 'Reference ID', '110px'),
          numberColumn('invoiceSubtotal', 'Invoice Subtotal', '140px'),
          numberColumn('invoiceCgstAmount', 'Invoice CGST Amount', '160px'),
          numberColumn('invoiceSgstAmount', 'Invoice SGST Amount', '160px'),
          numberColumn('invoiceRoundOff', 'Invoice Round Off', '140px'),
          numberColumn('invoiceTotalAmount', 'Invoice Total Amount', '160px'),
          dateTimeColumn('createdAt', 'Line Item Date'),
        ];

      case 'client-utilization':
        return [
          {
            key: 'clientName', 
            label: 'Client', 
            width: '220px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          {
            key: 'clientType',
            label: 'Type',
            width: '140px',
            render: (value: string) => (
              <Badge variant="secondary" rounded>
                {value?.replace('_', ' ').toUpperCase()}
              </Badge>
            ),
          },
          {
            key: 'palletsAllocated',
            label: 'Base Allocated',
            width: '140px',
            render: (value: number, row: any) => (
              <div className="font-semibold text-gray-900">
                {row.clientType === 'per_pallet' 
                  ? (value || 0) + ' pallets'
                  : ((value * 25) || 0) + ' sqft'
                }
              </div>
            ),
          },
          {
            key: 'palletsUsed',
            label: 'Base Used',
            width: '140px',
            render: (value: number, row: any) => {
              const baseAllocated = row.palletsAllocated || 0;
              const baseUsed = Math.min(value || 0, baseAllocated);
              return (
                <div className="font-semibold text-gray-900">
                  {row.clientType === 'per_pallet' 
                    ? baseUsed + ' pallets'
                    : (baseUsed * 25) + ' sqft'
                  }
                </div>
              );
            },
          },
          {
            key: 'palletsRemaining',
            label: 'Base Remaining',
            width: '140px',
            render: (value: number, row: any) => (
              <div className="font-semibold text-gray-900">
                {row.clientType === 'per_pallet' 
                  ? (value || 0) + ' pallets'
                  : ((value * 25) || 0) + ' sqft'
                }
              </div>
            ),
          },
          {
            key: 'additionalSpaceSqft',
            label: 'Additional Space',
            width: '150px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">
                {(value || 0) + ' sqft'}
              </div>
            ),
          },
          {
            key: 'additionalPalletsUsed',
            label: 'Additional Pallets Used',
            width: '180px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">
                {(value || 0) + ' pallets'}
              </div>
            ),
          },
          {
            key: 'utilization',
            label: 'Utilization',
            width: '220px',
            render: (value: number) => {
              const getBarColor = (val: number) => {
                if (val >= 90) return 'bg-red-500';
                if (val >= 70) return 'bg-yellow-500';
                return 'bg-green-500';
              };
              
              return (
                <div className="flex items-center">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                    <div
                      className={`h-2 rounded-full ${getBarColor(value)}`}
                      style={{ width: Math.min(value || 0, 100) + '%' }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-900">{(value?.toFixed(1) || 0) + '%'}</span>
                </div>
              );
            },
          },
        ];

      case 'additional-space':
        return [
          { 
            key: 'clientName', 
            label: 'Client Name', 
            width: '200px',
            render: (value: string) => (
              <div className="font-medium text-gray-900 truncate" title={value}>
                {value}
              </div>
            )
          },
          {
            key: 'clientType',
            label: 'Client Type',
            width: '130px',
            render: (value: string) => (
              <Badge variant="secondary" rounded>
                {value?.replace('_', ' ').toUpperCase()}
              </Badge>
            ),
          },
          {
            key: 'spaceSqft',
            label: 'Space/Pallets',
            width: '150px',
            render: (value: number, row: any) => (
              <div className="font-bold text-gray-900">
                {row.clientType === 'per_pallet' 
                  ? (row.palletCount?.toLocaleString() || 0) + ' pallets'
                  : (value?.toLocaleString() || 0) + ' sqft'
                }
              </div>
            ),
          },
          {
            key: 'startDate',
            label: 'Start Date',
            width: '140px',
            render: (value: string) => (
              <div className="text-sm text-gray-900">
                {value ? new Date(value).toLocaleDateString() : '-'}
              </div>
            ),
          },
          {
            key: 'endDate',
            label: 'End Date',
            width: '140px',
            render: (value: string, row: any) => (
              <div className="text-sm text-gray-900">
                {row.isActive ? (
                  <Badge variant="secondary" rounded>Active</Badge>
                ) : (
                  value ? new Date(value).toLocaleDateString() : '-'
                )}
              </div>
            ),
          },
          {
            key: 'durationDays',
            label: 'Duration (Days)',
            width: '140px',
            render: (value: number) => (
              <div className="font-semibold text-gray-900">
                {value?.toLocaleString() || 0}
              </div>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            width: '120px',
            render: (value: string, row: any) => {
              return row.isActive ? (
                <Badge variant="secondary" rounded>
                  Active
                </Badge>
              ) : (
                <Badge variant="secondary" rounded>
                  Released
                </Badge>
              );
            },
          },
          {
            key: 'grnNumber',
            label: 'GRN Number',
            width: '140px',
            render: (value: string) => (
              <div className="text-sm font-mono text-gray-900 truncate" title={value || '-'}>
                {value || '-'}
              </div>
            ),
          },
        ];

      default:
        return [];
    }
  };

  const allColumns = getColumns();
  const visibleColumns = selectedFields.length > 0
    ? allColumns.filter(col => selectedFields.includes(col.key))
    : allColumns;

  const unselectedFields = availableFields.filter(f => !selectedFields.includes(f.key));

  // Group unselected fields by category for the dropdown
  const unselectedByCategory = unselectedFields.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = [];
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof availableFields>);

  return (
    <div>
      {/* Table header: column count + Add Field button */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500 font-medium">
          {visibleColumns.length} column{visibleColumns.length !== 1 ? 's' : ''} visible
        </span>

        {unselectedFields.length > 0 && (
          <div className="relative">
            <button
              onClick={() => setShowAddField(prev => !prev)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-50 hover:bg-brand-100 text-brand-700 border border-brand-300 rounded-lg transition-colors font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Field
            </button>

            {showAddField && (
              <div className="absolute right-0 top-9 z-20 bg-white border border-gray-200 rounded-xl shadow-xl w-64 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Add Column</span>
                  <button onClick={() => setShowAddField(false)} className="text-gray-400 hover:text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(unselectedByCategory).map(([category, fields]) => (
                    <div key={category}>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1">{category}</p>
                      {fields.map(field => (
                        <button
                          key={field.key}
                          onClick={() => { onFieldToggle(field.key); setShowAddField(false); }}
                          className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-brand-50 hover:text-brand-700 rounded-lg transition-colors"
                        >
                          {field.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ResponsiveTable
        columns={visibleColumns}
        data={data}
        align="left"
        height="500px"
        emptyMessage={
          data.length === 0
            ? "No data available for the selected filters. Try adjusting your filter criteria."
            : "No data available"
        }
        onRowClick={handleRowClick}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoadingMore}
      />
    </div>
  );
}